/**
 * Yardım Merkezi (Kullanım Kılavuzu) içeriği.
 *
 * TEK KAYNAK: Panelin içindeki dokümantasyonun tüm metinleri buradadır.
 * Yeni bir anlatım eklemek veya video koymak için bu dosyayı düzenlemeniz yeterlidir;
 * ekstra bir kütüphane/altyapı gerekmez (changelog.ts ile aynı kalıp).
 *
 * VIDEO EKLEME:
 *   Her makalenin `videoUrl` alanına şunlardan birini yazabilirsiniz:
 *     - Yerel dosya:   '/static/videos/urun-ekleme.mp4'  (dosyayı public/static/videos/ altına koyun)
 *     - YouTube linki: 'https://www.youtube.com/watch?v=XXXX'  veya  'https://youtu.be/XXXX'
 *     - Vimeo linki:   'https://vimeo.com/XXXX'
 *   Alanı boş bırakırsanız (veya hiç yazmazsanız) "Video yakında" yer tutucusu görünür.
 *
 * ROL GÖRÜNÜRLÜĞÜ:
 *   `roles` alanı, o makaleyi hangi rollerin görebileceğini belirler.
 *   Örn. sadece şube yöneticisini ilgilendiren bir konu için [Role.BRANCH_ADMIN] yazın.
 */

import { Role } from '@/enums/role';
import { FeatureKey } from '@/types/Licensing.interface';

/** Bir makaledeki tek bir adım. */
export interface DocStep {
  /** Adımın açıklaması. */
  text: string;
  /** Ek bilgi (mavi ipucu kutusu). */
  note?: string;
  /** Dikkat edilmesi gereken uyarı (sarı/kırmızı kutu). */
  warning?: string;
}

/** Yardım merkezindeki tek bir anlatım/konu. */
export interface DocArticle {
  /** Benzersiz kimlik (URL/anahtar için, kebab-case). */
  id: string;
  /** Başlık. */
  title: string;
  /** Kısa özet (liste ve arama için). */
  summary: string;
  /** Bu makaleyi görebilecek roller. */
  roles: Role[];
  /** İlgili lisans özelliği (opsiyonel, rozet olarak gösterilir). */
  feature?: FeatureKey;
  /** Numaralı adımlar. */
  steps: DocStep[];
  /** Video kaynağı (yerel yol veya YouTube/Vimeo linki). Boşsa yer tutucu gösterilir. */
  videoUrl?: string;
  /** Ek ipuçları (madde madde). */
  tips?: string[];
}

/** Yardım merkezindeki bir bölüm (soldaki menü grubu). */
export interface DocSection {
  /** Benzersiz kimlik. */
  id: string;
  /** Bölüm başlığı. */
  title: string;
  /** MUI ikon adı (DocSidebar içindeki ICON_MAP ile eşleşir). */
  icon: string;
  /** Bölümdeki makaleler. */
  articles: DocArticle[];
}

/** Panelde şube yöneticisinin görebildiği tüm roller için varsayılan (herkes). */
const ALL_STAFF: Role[] = [
  Role.SUPER_ADMIN,
  Role.COMPANY_ADMIN,
  Role.BRANCH_ADMIN,
  Role.EMPLOYEE
];

/** Şube ve şirket yöneticileri (çalışan hariç yönetim rolleri). */
const MANAGERS: Role[] = [
  Role.SUPER_ADMIN,
  Role.COMPANY_ADMIN,
  Role.BRANCH_ADMIN
];

export const DOCS: DocSection[] = [
  /* ═══════════════════════════════════════════════════════════════
     ① BAŞLARKEN
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'baslarken',
    title: 'Başlarken',
    icon: 'RocketLaunch',
    articles: [
      {
        id: 'giris-yapma',
        title: 'Panele Giriş Yapma',
        summary:
          'E-posta/telefon ve şifrenizle yönetim paneline nasıl giriş yaparsınız.',
        roles: ALL_STAFF,
        videoUrl: 'https://youtu.be/Px93-VLE60o',
        steps: [
          {
            text: 'Tarayıcınızdan panel adresini açın. Karşınıza giriş ekranı gelir.'
          },
          {
            text: 'E-posta adresinizi (veya telefon numaranızı) ve şifrenizi ilgili alanlara yazın.',
            note: 'Giriş bilgileriniz size işletme/şirket yöneticiniz tarafından tanımlanır. Bilgileriniz yoksa yöneticinizden talep edin.'
          },
          {
            text: '"Giriş Yap" düğmesine tıklayın. Bilgiler doğruysa doğrudan Ana Pano ekranına yönlendirilirsiniz.'
          },
          {
            text: 'Şifrenizi unuttuysanız giriş ekranındaki "Şifrenizi mi unuttunuz?" bağlantısını kullanın.',
            warning:
              'Şifrenizden emin değilseniz denemeye devam etmek yerine "Şifrenizi mi unuttunuz?" bağlantısıyla sıfırlamanız daha güvenlidir.'
          }
        ],
        tips: [
          'Ortak/halka açık bir bilgisayarda işiniz bitince mutlaka oturumu kapatın.',
          'Çıkış yapmak için sağ üstteki kullanıcı kutusuna tıklayıp "Çıkış Yap"ı seçin.'
        ]
      },
      {
        id: 'sifre-sifirlama',
        title: 'Şifre Sıfırlama (Şifrenizi mi unuttunuz?)',
        summary:
          'Şifrenizi unuttuğunuzda telefonunuza gelen kod ile yeni şifre belirleme.',
        roles: ALL_STAFF,
        videoUrl: 'https://youtu.be/Wod2FCFFK6g',
        steps: [
          {
            text: 'Giriş ekranında "Şifrenizi mi unuttunuz?" bağlantısına tıklayın.'
          },
          {
            text: 'Kayıtlı telefon numaranızı girin ve doğrulama kodu isteyin.',
            note: 'Doğrulama kodu SMS ile telefonunuza gönderilir.'
          },
          {
            text: 'Telefonunuza gelen doğrulama kodunu ilgili alana yazın.'
          },
          {
            text: 'Yeni şifrenizi belirleyin ve tekrar yazarak onaylayın. Ardından yeni şifrenizle giriş yapın.',
            warning:
              'Doğrulama kodunun geçerlilik süresi sınırlıdır. Kod gelmezse "Kodu Tekrar Gönder" ile yeni kod isteyin.'
          }
        ]
      },
      {
        id: 'panel-genel-bakis',
        title: 'Panel Arayüzüne Genel Bakış',
        summary:
          'Sol menü, üst bar, bildirim zili ve ana çalışma alanının tanıtımı.',
        roles: ALL_STAFF,
        videoUrl: 'https://youtu.be/lItoAh8GY_w',
        steps: [
          {
            text: 'SOL MENÜ (kenar çubuğu): Tüm modüllere buradan ulaşırsınız. Menü, "Panolar", "Yönetim", "Entegrasyonlar" ve "İndirmeler" gibi gruplara ayrılmıştır.',
            note: 'Bazı menü başlıklarına tıklandığında alt menüler açılır (örn. "Ürün Yönetimi", "Üyelik Yönetimi").'
          },
          {
            text: 'ÜST BAR: Sağ üstte kullanıcı kutusu (profiliniz ve çıkış), bildirim zili ve — yetkiniz varsa — şube seçim menüsü bulunur.'
          },
          {
            text: 'ANA ÇALIŞMA ALANI: Seçtiğiniz modülün içeriği ortadaki geniş alanda açılır (tablolar, formlar, grafikler).'
          },
          {
            text: 'KİLİT SİMGESİ: Bir menü öğesinin yanında asma kilit simgesi ve soluk görünüm varsa, o özellik mevcut abonelik planınıza dahil değildir.',
            note: 'Kilitli bir özelliği kullanmak isterseniz şirket yöneticinizle veya destek ekibiyle iletişime geçin.'
          }
        ],
        tips: [
          'Dar ekranlarda sol menü otomatik gizlenir; sol üstteki menü düğmesiyle açıp kapatabilirsiniz.',
          'Gördüğünüz menüler rolünüze göre değişir — şube yöneticisi olarak şirket/şube listesi gibi bazı yönetim menülerini görmezsiniz.'
        ]
      },
      {
        id: 'sube-secimi',
        title: 'Şube (Lokasyon) Seçimi',
        summary:
          'Birden fazla şubeniz varsa hangi şube üzerinde çalıştığınızı seçme.',
        roles: MANAGERS,
        videoUrl: 'https://youtu.be/b7euTTNzifA',
        steps: [
          {
            text: 'Üst bardaki şube/lokasyon seçicisine tıklayın.'
          },
          {
            text: 'Yetkili olduğunuz şubelerden birini seçin. Panel, seçtiğiniz şubenin verilerini gösterecek şekilde güncellenir.',
            note: 'Şube yöneticisi olarak yalnızca size atanmış şubeleri görürsünüz; başka şubelerin verilerine erişemezsiniz.'
          },
          {
            text: 'Raporlar, siparişler ve ürünler gibi ekranlarda ayrıca "Lokasyon" filtresiyle de şubeye göre süzme yapabilirsiniz.'
          }
        ],
        tips: [
          'Yalnızca tek şubeye atanmışsanız şube seçimi otomatik yapılır, ek işlem gerekmez.'
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ② ANA PANO & RAPORLAR
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'ana-pano-raporlar',
    title: 'Ana Pano & Raporlar',
    icon: 'InsertChart',
    articles: [
      {
        id: 'dashboard-genel',
        title: 'Ana Pano (Dashboard) Nedir?',
        summary:
          'İşletmenizin özet göstergelerini, satışları ve grafikleri tek ekranda görme.',
        roles: MANAGERS,
        feature: FeatureKey.REPORTS,
        videoUrl: 'https://youtu.be/U-wj-XH7z2M',
        steps: [
          {
            text: 'Sol menüden "Ana Panel" (Dashboard) öğesine tıklayın. Giriş yaptığınızda da varsayılan olarak bu ekran açılır.'
          },
          {
            text: 'Ekranın üstünde filtre alanı, altında özet kartları (KPI) ve sekmeler halinde grafikler yer alır.'
          },
          {
            text: 'Sağ üstteki "Verileri Yenile" düğmesiyle göstergeleri anlık olarak tazeleyebilirsiniz.'
          }
        ],
        tips: [
          'Ana Pano, seçili şube ve tarih aralığına göre canlı hesaplanır; filtreleri değiştirdiğinizde tüm kartlar ve grafikler güncellenir.'
        ]
      },
      {
        id: 'dashboard-filtreleri',
        title: 'Rapor Filtrelerini Kullanma',
        summary:
          'Şube, ödeme tipi, sipariş durumu ve tarih aralığına göre raporları süzme.',
        roles: MANAGERS,
        feature: FeatureKey.REPORTS,
        videoUrl: 'https://youtu.be/cLqwaPH1I5E',
        steps: [
          {
            text: 'Ana Pano üstündeki filtre çubuğunu kullanın. "Şube seç" ile belirli bir lokasyonu (veya "Tüm Şubeler") seçin.'
          },
          {
            text: '"Ödeme Tipi seç" ile nakit, kart vb. ödeme türüne göre süzün.'
          },
          {
            text: '"Sipariş Durumu seç" ile hazırlanıyor, hazır, teslim edildi, iptal gibi duruma göre filtreleyin.'
          },
          {
            text: 'Tarih aralığını seçerek (örn. bugün, bu hafta, bu ay veya özel tarihler) raporu istediğiniz döneme daraltın.'
          },
          {
            text: 'İstediğiniz sonucu göremezseniz "Filtreleri Sıfırla" ile tüm filtreleri temizleyin.',
            note: 'Filtreler tüm kartları ve grafikleri aynı anda etkiler; tutarlı bir dönem seçmek doğru karşılaştırma sağlar.'
          }
        ]
      },
      {
        id: 'kpi-kartlari',
        title: 'Özet Göstergeler (KPI Kartları)',
        summary:
          'Bekleyen, hazır ve iptal edilen siparişleri tek bakışta görme.',
        roles: MANAGERS,
        feature: FeatureKey.REPORTS,
        videoUrl: 'https://youtu.be/9SI6oNHDfg8',
        steps: [
          {
            text: 'Filtre çubuğunun altındaki kartlar anlık sipariş durumunuzu özetler: Bekleyen Siparişler, Hazır Siparişler ve İptal Edilmiş Siparişler.'
          },
          {
            text: 'Kartların üzerine geldiğinizde (hover) ek açıklama/ipucu görünür.'
          },
          {
            text: 'Bu kartlar seçili şube ve tarih aralığına göre hesaplanır; yoğun saatlerde mutfak/servis yükünü takip etmek için idealdir.'
          }
        ]
      },
      {
        id: 'satis-analiz-sekmeleri',
        title: 'Satış ve Analiz Sekmeleri',
        summary:
          'Genel bakış, satış trendleri, en çok satanlar, şube performansı ve müşteri analizleri.',
        roles: MANAGERS,
        feature: FeatureKey.ANALYTICS,
        videoUrl: 'https://youtu.be/2PBLdfHAx7k',
        steps: [
          {
            text: '"Analitik ve Raporlar": Satışa genel bakış (sipariş, günlük giriş, üyelik, bilet ve paket satışları) ile türüne göre satış dağılımını gösterir.'
          },
          {
            text: '"Satış Trendleri": Seçili dönemdeki satışın zaman içindeki değişimini grafikle sunar.'
          },
          {
            text: '"En Çok Satan Ürünler": Dönem içinde en çok satılan ürünlerinizi sıralar; menü kararları için kullanışlıdır.'
          },
          {
            text: '"Şube Performansı": Birden çok şubeniz varsa şubeleri karşılaştırır.',
            note: 'Şube yöneticisi olarak yalnızca yetkili olduğunuz şube(ler) kapsamında veri görürsünüz.'
          },
          {
            text: '"Müşteri Demografisi" ve "Müşteri Segmentasyonu": Müşteri kitlenizin dağılımını ve segmentlerini analiz eder.'
          }
        ],
        tips: [
          'Sekmeler arasında geçiş yaptığınızda üstteki filtreler korunur; aynı dönemi farklı açılardan inceleyebilirsiniz.',
          'CİRO KURALLARI: Zayi/iç tüketim kayıtları (0 TL) hiçbir gelir rakamına dahil edilmez. İade edilen siparişler ve üyelik iadeleri negatif olarak yansır, cirodan düşer. Üyelik gelirleri satış/uzatma/iade işlemleri bazında hesaplanır.'
        ]
      },
      {
        id: 'zayi-ic-tuketim-raporu',
        title: 'Zayi / İç Tüketim Raporu',
        summary:
          'Personel yemeği, ikram, fire ve SKT imha gibi 0 TL kayıtların takibi.',
        roles: MANAGERS,
        feature: FeatureKey.REPORTS,
        videoUrl: 'https://youtu.be/1YflvDEqVlw',
        steps: [
          {
            text: 'Ana Pano "Analitik ve Raporlar" sekmesindeki "Zayi / İç Tüketim" kartına bakın.'
          },
          {
            text: 'Bu kart; Toplam Adet ve Toplam Maliyet ile birlikte en çok zayi olan ürünlerin listesini gösterir.',
            note: 'Tutarlar liste (satış) fiyatı değil, ürünün maliyeti üzerinden hesaplanır.'
          },
          {
            text: 'Zayi tipleri: Personel Yemeği, İkram, Fire/Kırılan ve SKT (son kullanma) İmha. Böylece kaybın nereden geldiğini ayrıştırabilirsiniz.'
          },
          {
            text: 'Zayi kayıtları 0 TL tutarındadır ve CİROYA DAHİL EDİLMEZ; yalnızca maliyet/kayıp takibi amacıyla raporlanır.',
            warning:
              'Zayi/iç tüketim kaydı bu panelden oluşturulmaz; kayıtlar satış (POS/masaüstü) uygulamasından girilir ve buraya rapor olarak yansır.'
          }
        ],
        tips: [
          'Yüksek zayi oranı, porsiyon/stok yönetimi veya SKT takibi için bir uyarı işareti olabilir.'
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ③ ÜRÜN & MENÜ YÖNETİMİ
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'urun-menu-yonetimi',
    title: 'Ürün & Menü Yönetimi',
    icon: 'Restaurant',
    articles: [
      {
        id: 'urun-listesi',
        title: 'Ürün Listesine Genel Bakış',
        summary: 'Ürünlerinizi listeleme, arama, filtreleme ve toplu işlemler.',
        roles: MANAGERS,
        feature: FeatureKey.PRODUCTS,
        videoUrl: 'https://youtu.be/BtuAXl9nchQ',
        steps: [
          {
            text: 'Sol menüden "Ürün Yönetimi" başlığını açın ve "Ürün Listesi"ne tıklayın.'
          },
          {
            text: 'Tablo; ürün görseli, adı, fiyatı, kategorisi, lokasyonu ve durumunu gösterir. Üstteki arama kutusuyla ürün adına göre arayabilirsiniz.'
          },
          {
            text: 'Filtreler: durum (Aktif/Pasif), tarih aralığı ve lokasyon (şube). İstediğiniz ürünleri hızlıca daraltın.'
          },
          {
            text: 'Toplu işlem: Satır başındaki kutucuklarla birden fazla ürün seçip topluca silebilir veya durumlarını (Aktif/Pasif) topluca güncelleyebilirsiniz.',
            note: 'Bir ürünü silmeye çalıştığınızda başka kayıtlara bağlıysa (örn. bir menüde kullanılıyorsa) uyarı çıkar; onaylarsanız "zorla sil" ile devam edebilirsiniz.'
          }
        ]
      },
      {
        id: 'urun-ekleme',
        title: 'Yeni Ürün Ekleme',
        summary:
          'Ürün formundaki tüm alanlar: fiyat, maliyet, sadakat puanı, stok ayarları ve alerjenler.',
        roles: MANAGERS,
        feature: FeatureKey.PRODUCTS,
        videoUrl: 'https://youtu.be/aEpXRDgFQZY',
        steps: [
          {
            text: 'Ürün Listesi ekranında sağ üstteki "Yeni Ürün" düğmesine tıklayın.'
          },
          {
            text: '"İsim" (zorunlu) ve "Açıklama" alanlarını doldurun.'
          },
          {
            text: '"Temel Fiyat" (zorunlu, ₺) ürünün satış fiyatıdır. "Maliyet Fiyatı" (opsiyonel) ürünün alış/üretim maliyetidir; kâr ve zayi hesapları için kullanılır.',
            warning:
              'Maliyet fiyatı satış fiyatından yüksek girilirse "zarar" uyarısı gösterilir. Değerleri kontrol edin.'
          },
          {
            text: '"Sadakat Puanı": Bu üründen müşterinin kazanacağı puanı belirler (mobil sadakat aktifse).'
          },
          {
            text: '"Stok Birimi" (adet, kg, lt vb.) ve "Kategori" (zorunlu) seçin. Kategori yoksa önce Kategori Yönetimi\'nden ekleyin.'
          },
          {
            text: '"Şube Seçin": Ürünü yalnızca belirli bir şubede satışa çıkarmak için şube seçin; tüm şubelerde geçerli olması için "Tüm şubeler (şirket geneli)" bırakın.',
            note: 'Bu alan yalnızca şirketinizin ürün stratejisi buna izin veriyorsa görünür (bkz. "Ürün Yönetim Stratejisi").'
          },
          {
            text: '"Bu ürün bir menü" anahtarı: Ürünü, içinden seçim yapılan bir menü/kombin olarak tanımlar (bkz. "Menü Yönetimi").'
          },
          {
            text: 'STOK AYARLARI: "Stok Takibini Etkinleştir" açıldığında "SKT Takibi Kullan (Parti/Lot)" ve "Negatif Stoka İzin Ver" seçenekleri belirir.'
          },
          {
            text: 'ALERJENLER (Türk Gıda Kodeksi Ek-1): Üründe bulunan alerjen gruplarını çip listesinden seçin. İsterseniz "Alerjen notu" ekleyin (örn. "Fındık izi içerebilir"). Seçilenler menülerde, POS ve QR menüde gösterilir.'
          },
          {
            text: '"Durum"u seçin (Aktif/Pasif), ürün görselini yükleyin ve "Kaydet"e tıklayın.',
            note: 'Yeni üründe görsel yüklemek önerilir; düzenlemede yeni görsel seçmezseniz mevcut görsel korunur.'
          }
        ],
        tips: [
          'ÖNEMLİ KURAL: Şube seçilerek oluşturulan ürün YALNIZCA o şubede görünür ve satılır; diğer şubeler bu ürünü görmez.',
          'ÖNEMLİ KURAL: Şube yöneticisi, ürün stratejisi "Merkezi" veya "Sadece Fiyat Esnek" ise yeni ürün OLUŞTURAMAZ; strateji izin veriyorsa da yalnızca KENDİ şubesinin oluşturduğu ürünleri düzenleyebilir. Şirket geneli ürünler şube yöneticisi için salt-okunurdur — şubenize özel fiyat için "Şube Özelleştirmeleri"ni kullanın.'
        ]
      },
      {
        id: 'kategori-yonetimi',
        title: 'Kategori Yönetimi',
        summary: 'Ürünleri gruplamak için kategori ekleme ve düzenleme.',
        roles: MANAGERS,
        feature: FeatureKey.PRODUCTS,
        videoUrl: 'https://youtu.be/aWfl8HJ3czI',
        steps: [
          {
            text: 'Sol menüden "Ürün Yönetimi > Kategori Yönetimi"ni açın.'
          },
          {
            text: 'Tablo; kategori görseli, adı, lokasyonu, durumu ve içindeki ürün sayısını gösterir.'
          },
          {
            text: '"Yeni Kategori" ile ekleyin: "İsim", (yetkinizdeyse) "Şirket", "Durum" ve görsel.'
          },
          {
            text: 'Var olan kategoriyi düzenleyip silebilir; birden fazla kategoriyi seçip toplu silme/durum güncelleme yapabilirsiniz.',
            warning:
              'ÖNEMLİ KURAL: Kategoriler şirket genelinde yönetilir. Şube yöneticisi kategorileri yalnızca GÖRÜNTÜLER; ekleme/düzenleme/silme düğmeleri şube yöneticisinde görünmez. Yeni kategori ihtiyacınızı şirket yöneticinize iletin.'
          }
        ]
      },
      {
        id: 'urun-ozellikleri',
        title: 'Ürün Özellikleri ve Varyantlar',
        summary:
          'Boyut, sıcaklık, ekstra gibi seçenek gruplarını (varyant) tanımlama.',
        roles: MANAGERS,
        feature: FeatureKey.PRODUCTS,
        videoUrl: 'https://youtu.be/V7q4v-lyIgk',
        steps: [
          {
            text: 'Ürün Listesi\'nde bir ürüne tıklayarak ürün detayına girin ve "Özellikler" sekmesini açın.'
          },
          {
            text: '"Özellik Ekle" ile yeni bir grup oluşturun. "Yeni Grup Adı" (örn. Boyut, Sıcaklık) ve ilk "Seçenek Adı"nı (örn. Küçük, Orta, Büyük) girin.'
          },
          {
            text: '"Ekstra Fiyat": O seçenek için ek ücret (₺). Boş bırakılırsa ek ücret alınmaz. "Sıralama": Seçeneğin grup içindeki gösterim sırası (küçük sayı önce).'
          },
          {
            text: '"Varsayılan Seçenek" anahtarı: Grup açıldığında seçili gelen seçenek (grup başına yalnız bir tane). "Zorunlu Grup": Müşterinin mutlaka seçim yapmasını zorunlu kılar.'
          },
          {
            text: '"Oluştur" ile kaydedin veya "Oluştur ve Yeni Ekle" ile arka arkaya birden çok seçenek ekleyin. Bir gruba sonradan "Seçenek Ekle" ile yeni seçenek de ekleyebilirsiniz.'
          }
        ],
        tips: [
          'Aynı özellikleri başka üründe tekrar kurmamak için "Özellik İçe Aktar" ile mevcut bir gruptan kopyalayabilirsiniz.'
        ]
      },
      {
        id: 'sube-ozel-fiyat',
        title: 'Şubeye Özel Fiyat ve Durum',
        summary:
          'Bir ürünün fiyatını veya durumunu yalnızca belirli bir şube için değiştirme.',
        roles: MANAGERS,
        feature: FeatureKey.PRODUCTS,
        videoUrl: 'https://youtu.be/OIVCbBOptXA',
        steps: [
          {
            text: 'Ürün detayında "Şube Özelleştirmeleri" sekmesini açın.'
          },
          {
            text: 'İlgili şube için özel bir fiyat ve/veya durum (satışta/kapalı) tanımlayın. Böylece aynı ürün farklı şubede farklı fiyatla satılabilir.',
            note: 'Bu özellik yalnızca şirketinizin ürün stratejisi şube özelleştirmelerine izin veriyorsa kullanılabilir.'
          },
          {
            text: 'Özelleştirme tanımlanmayan şubelerde ürün, temel (merkezi) fiyat ve durumla satılır.'
          }
        ]
      },
      {
        id: 'menu-yonetimi',
        title: 'Menü (Kombin/Set) Yönetimi',
        summary:
          'İçinden seçim yapılan menüler oluşturma; grup, seçim türü ve min/maks kuralları.',
        roles: MANAGERS,
        feature: FeatureKey.PRODUCTS,
        videoUrl: 'https://youtu.be/GS6Mo-Posas',
        steps: [
          {
            text: 'Sol menüden "Ürün Yönetimi > Menü Yönetimi"ni açın. Sol tarafta menüler listelenir; birine tıklayınca sağda içeriği görünür.'
          },
          {
            text: '"Yeni Menü Oluştur" ile menü, "Grup Oluştur" ile menü içindeki seçim grupları (örn. Ana Yemek, İçecek) oluşturulur.'
          },
          {
            text: 'Bir gruba öğe eklerken: "Ürün Seç" (aramalı), "Grup", "Ekstra Fiyat", "Sıralama" alanlarını doldurun.'
          },
          {
            text: '"Seçim Türü"nü belirleyin — Zorunlu: müşteri mutlaka seçer (ana yemek). İsteğe Bağlı: isterse seçer (sos, ekstra). Seçilebilir: alternatifler arasından birini seçer (içecek türü).'
          },
          {
            text: '"Minimum Seçim" / "Maksimum Seçim" ile o gruptan kaç öğe seçilebileceğini sınırlayın; "Varsayılan Seçim" ile öntanımlı öğeyi (Evet/Hayır) belirleyin.'
          },
          {
            text: 'Çok sayıda öğeyi hızlı eklemek için "Toplu Ekle"yi, sonucu görmek için "Menü Önizleme"yi kullanın.'
          }
        ]
      },
      {
        id: 'urun-stratejisi',
        title: 'Ürün Yönetim Stratejisi',
        summary:
          'Şubelerin ürün ve fiyatları ne kadar değiştirebileceğini belirleyen ayar.',
        roles: MANAGERS,
        feature: FeatureKey.PRODUCTS,
        videoUrl: 'https://youtu.be/ZRKfsgycMac',
        steps: [
          {
            text: 'Bu sayfaya sağ üstteki kullanıcı menüsünden "Şirket Ürün Ayarları" bağlantısıyla ulaşılır (yalnızca şirket yöneticisi menüsünde görünür). "Strateji Seçimi" bölümünden birini seçin:'
          },
          {
            text: 'Merkezi: Tüm ürünler merkezi yönetilir; şubeler fiyat/detay değiştiremez. Sadece Fiyat Esnek: Şubeler yalnızca fiyatı değiştirebilir. Karma: Şubeler hem fiyatı hem detayı değiştirebilir. Bağımsız: Şubeler tamamen bağımsızdır ve kendi ürünlerini oluşturabilir.'
          },
          {
            text: 'Ek anahtarlar: "Şube Özel Ayarlarına İzin Ver", "Özel Ürün Oluşturmaya İzin Ver" ve "Onay Gerektir". Ayarları "Kaydet" ile uygulayın.'
          },
          {
            text: 'Bu ayar, ürün eklerken "Şube Seçin" alanının görünmesini ve şube yöneticilerinin ürün/fiyat değiştirip değiştiremeyeceğini belirler.',
            warning:
              'Ürün stratejisi genellikle şirket yöneticisi tarafından belirlenir. Şube yöneticisi bu sayfayı görüntüleyebilir ancak değiştirme yetkisi yoksa kaydetme sırasında hata alır.'
          }
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ④ STOK & ENVANTER
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'stok-envanter',
    title: 'Stok & Envanter',
    icon: 'Inventory2',
    articles: [
      {
        id: 'stok-genel',
        title: 'Stok Yönetimine Genel Bakış',
        summary:
          'Envanterinizi ve stok seviyelerinizi görüntüleme; satırdaki işlem düğmeleri.',
        roles: MANAGERS,
        feature: FeatureKey.STOCK,
        videoUrl: 'https://youtu.be/jYDOV8FS8Lk',
        steps: [
          {
            text: 'Sol menüden "Ürün Yönetimi > Stok Yönetimi"ne girin. Ekran, ürün bazında güncel stok seviyelerini listeler.'
          },
          {
            text: 'Her satırda ürün, depo ve mevcut stok görünür. Parti (SKT) takipli ürünlerde "Parti" göstergesiyle partileri açıp inceleyebilirsiniz.'
          },
          {
            text: 'Satır üzerindeki düğmelerle o ürün için Stok Ekle, Stok Çıkar, Stok Düzelt, Stok Transfer Et ve Stok Geçmişi işlemlerini yaparsınız (aşağıdaki konular).'
          }
        ]
      },
      {
        id: 'stok-baslatma',
        title: 'Yeni Stok Kaydı Başlatma',
        summary:
          'Bir ürün için ilk kez stok kaydı açma (başlangıç miktarı ve eşikler).',
        roles: MANAGERS,
        feature: FeatureKey.STOCK,
        videoUrl: 'https://youtu.be/-H63lMmPzGQ',
        steps: [
          {
            text: 'Stok Yönetimi ekranında "Yeni Stok Kaydı Başlat" işlemini açın.'
          },
          {
            text: '"Şirket" ve "Şube"yi seçin, ardından "Ürün Seçin" ve "Depo"yu belirleyin.',
            note: 'Listede yalnızca stok takibi açık ürünler görünür. Ürünün stok takibi kapalıysa önce ürün formundan açın.'
          },
          {
            text: '"Başlangıç Miktarı"nı girin. "Stok Birimi" üründen otomatik gelir.'
          },
          {
            text: 'İsteğe bağlı "Minimum Eşik" / "Maksimum Eşik" değerleriyle kritik stok uyarı seviyelerini belirleyin.'
          },
          {
            text: 'Ürün parti/SKT takipliyse "Son Kullanma Tarihi" (zorunlu), "Üretim Tarihi" ve "Tedarikçi Parti Numarası" alanlarını doldurup kaydedin.'
          }
        ]
      },
      {
        id: 'stok-ekleme',
        title: 'Stok Ekleme (Giriş)',
        summary: 'Mevcut bir stoğa mal girişi yapma.',
        roles: MANAGERS,
        feature: FeatureKey.STOCK,
        videoUrl: 'https://youtu.be/Hz5TbcDu40Q',
        steps: [
          {
            text: 'İlgili ürün satırında "Stok Ekle" düğmesine tıklayın. Üstte ürün, depo ve mevcut stok bilgisi gösterilir.'
          },
          {
            text: '"Eklenecek Miktar"ı girin ve isterseniz "Sebep (Opsiyonel)" yazın.'
          },
          {
            text: 'Parti/SKT takipli üründe yeni parti için "Son Kullanma Tarihi", "Üretim Tarihi", "Tedarikçi Parti Numarası", tedarikçi bilgisi ve notu girebilirsiniz.'
          },
          {
            text: 'Ekranda oluşacak "Yeni Stok" değerini kontrol edip "Stok Ekle" ile kaydedin.'
          }
        ]
      },
      {
        id: 'stok-cikarma',
        title: 'Stok Çıkarma (Çıkış)',
        summary: 'Kullanım/fire için stoktan düşme; parti takipli üründe FEFO.',
        roles: MANAGERS,
        feature: FeatureKey.STOCK,
        videoUrl: 'https://youtu.be/oo2XCat0iZM',
        steps: [
          {
            text: 'İlgili satırda "Stok Çıkar" düğmesine tıklayın.'
          },
          {
            text: '"Çıkarılacak Miktar"ı girin ve gerekiyorsa "Sebep (Opsiyonel)" yazın.'
          },
          {
            text: "Parti takipli ürünlerde çıkış, İlk Süresi Dolan İlk Çıkar (FEFO) mantığıyla otomatik olarak SKT'si en yakın partilerden yapılır.",
            note: 'Negatif stoğa izin verilmeyen üründe, mevcuttan fazla çıkarmaya çalışırsanız işlem engellenir ve uyarı görürsünüz.'
          }
        ]
      },
      {
        id: 'stok-duzeltme',
        title: 'Stok Düzeltme (Sayım)',
        summary: 'Fiziki sayım sonrası stoğu doğru değere sabitleme.',
        roles: MANAGERS,
        feature: FeatureKey.STOCK,
        videoUrl: 'https://youtu.be/ofCwcL4LXzQ',
        steps: [
          {
            text: 'İlgili satırda "Stok Düzelt" düğmesine tıklayın.'
          },
          {
            text: '"Yeni Miktar" alanına sayımda bulunan gerçek stoğu yazın. Sistem "Fark"ı otomatik hesaplayıp gösterir.'
          },
          {
            text: '"Sebep (Zorunlu)" alanını doldurun (örn. dönem sayımı, kayıp).',
            warning:
              'Stok düzeltmede sebep girmek zorunludur; boş bırakılırsa kaydedilmez.'
          },
          {
            text: 'İsterseniz "Minimum Eşik" / "Maksimum Eşik" değerlerini de buradan güncelleyip kaydedin.'
          }
        ]
      },
      {
        id: 'stok-transfer',
        title: 'Şubeler Arası Stok Transferi',
        summary: 'Bir şubeden başka bir şube/depoya stok aktarma.',
        roles: MANAGERS,
        feature: FeatureKey.STOCK,
        videoUrl: 'https://youtu.be/H1affRwZusA',
        steps: [
          {
            text: 'İlgili satırda "Stok Transfer Et" düğmesine tıklayın. Üstte ürün, mevcut stok ve gönderen şube görünür.'
          },
          {
            text: 'Hedefi seçin: "Şirket", "Alıcı Şube" ve "Alıcı Depo". Ardından transfer edilecek miktarı girin.',
            note: 'Yalnızca yetkili olduğunuz şubeler arasında transfer yapabilirsiniz.'
          },
          {
            text: 'Onayladığınızda stok, gönderen depodan düşülüp alıcı depoya eklenir; her iki tarafta hareket kaydı oluşur.'
          }
        ]
      },
      {
        id: 'parti-skt-yonetimi',
        title: 'Parti / SKT Yönetimi ve İmha',
        summary:
          'Partiyi karantinaya alma, geri çağırma veya imha etme (sebep zorunlu).',
        roles: MANAGERS,
        feature: FeatureKey.BATCHES,
        videoUrl: 'https://youtu.be/NTK5TnW61HU',
        steps: [
          {
            text: "Stok Yönetimi'nde parti takipli bir ürünün partilerini açın ve ilgili partinin durum işlemini seçin."
          },
          {
            text: '"Karantinaya Al": Partiyi geçici olarak satıştan/kullanımdan çeker. "Geri Çağır": Sorunlu partiyi geri çağırır. "İmha Et": Partiyi kalıcı olarak imha eder.'
          },
          {
            text: 'Her işlemde bir sebep girmeniz istenir (örn. SKT geçti, hasar).',
            warning:
              'Bu işlemler stoğu doğrudan etkiler ve SKT imhaları zayi raporuna yansır. Sebep girmeden onaylanamaz.'
          }
        ]
      },
      {
        id: 'stok-gecmisi',
        title: 'Stok Hareket Geçmişi',
        summary:
          'Giriş/çıkış hareketlerini tablo ve grafikle inceleme; Excel dışa aktarma.',
        roles: MANAGERS,
        feature: FeatureKey.STOCK,
        videoUrl: 'https://youtu.be/PBLDQeT2adk',
        steps: [
          {
            text: 'Bir stok satırında "Stok Geçmişi" düğmesine tıklayın.'
          },
          {
            text: 'Üstteki özet kartları dönemin Toplam Giriş, Toplam Çıkış, Net Değişim ve hareket sayısını gösterir.'
          },
          {
            text: '"Hareketler" sekmesinde tüm giriş/çıkış/düzeltme/transfer kayıtlarını; "Grafik" sekmesinde zaman içindeki değişimi görürsünüz.'
          },
          {
            text: 'Kayıtları raporlamak için "Excel İndir" ile dışa aktarın.'
          }
        ]
      },
      {
        id: 'depo-yonetimi',
        title: 'Depo Yönetimi',
        summary: 'Şubeye ait depo tanımlama ve varsayılan depo belirleme.',
        roles: MANAGERS,
        feature: FeatureKey.WAREHOUSE,
        videoUrl: 'https://youtu.be/8SKcqtXWUzg',
        steps: [
          {
            text: 'Sol menüden "Ürün Yönetimi > Depo Yönetimi"ne girin ve "Depo Ekle" ile yeni depo oluşturun.'
          },
          {
            text: 'Alanlar: "Depo Adı" (örn. Ana Depo), (yetkinizdeyse) "Şirket" ve "Şube", "Açıklama".'
          },
          {
            text: '"Aktif" ve "Varsayılan Depo" anahtarlarını ayarlayın. Varsayılan depo, stok işlemlerinde öntanımlı seçilir.'
          }
        ]
      },
      {
        id: 'hammadde-yonetimi',
        title: 'Hammadde (İçerik) Yönetimi',
        summary:
          'Satılmayan ama reçetelerde kullanılan hammaddeleri tanımlama.',
        roles: MANAGERS,
        feature: FeatureKey.INGREDIENTS,
        videoUrl: 'https://youtu.be/ZzjYKYz2fVc',
        steps: [
          {
            text: 'Sol menüden "Ürün Yönetimi > Hammadde Yönetimi"ne girin ve yeni hammadde ekleyin.'
          },
          {
            text: 'Alanlar: "Hammadde Adı", "Açıklama", "Maliyet Fiyatı", "Stok Birimi" ve "Kategori".'
          },
          {
            text: '"Satışa Uygun" anahtarı: Hammadde doğrudan satılabiliyorsa açın; açtığınızda "Temel Fiyat" alanı görünür.'
          },
          {
            text: 'Gerekirse "Stok Takibini Etkinleştir", "SKT Takibi Kullan" ve "Negatif Stoka İzin Ver" ayarlarını yapıp "Durum" seçerek kaydedin.'
          }
        ],
        tips: [
          'Hammaddeler, reçetelerde ürün maliyetini ve stok düşümünü otomatik hesaplamak için kullanılır.'
        ]
      },
      {
        id: 'recete-yonetimi',
        title: 'Reçete Yönetimi',
        summary:
          'Bir ürünün hangi hammaddelerden, ne miktarda üretildiğini tanımlama.',
        roles: MANAGERS,
        feature: FeatureKey.RECIPE,
        videoUrl: 'https://youtu.be/3zaXl0qiUdE',
        steps: [
          {
            text: 'Sol menüden "Ürün Yönetimi > Reçete Yönetimi"ne girin ve yeni reçete oluşturun. Form sekmelere ayrılmıştır.'
          },
          {
            text: '"Temel Bilgiler": "Bitmiş Ürün" (reçetenin ürettiği ürün), "Verim Miktarı" ve "Verim Birimi" (bir üretimde çıkan miktar), açıklama ve aktiflik.'
          },
          {
            text: '"Zamanlama": "Hazırlık Süresi" ve "Pişirme Süresi". "Talimatlar": Üretim adımlarının metni.'
          },
          {
            text: '"Hammaddeler": Reçetede kullanılan her hammaddeyi seçip miktarını girin. Böylece üretimde stok otomatik düşülür ve maliyet hesaplanır.',
            warning:
              'Reçete tanımları genellikle şirket düzeyinde yönetilir. Şube yöneticisi reçeteleri görüntüleyip üretim yapabilir; reçete düzenleme yetkisi kısıtlı olabilir.'
          }
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ⑤ SİPARİŞ YÖNETİMİ
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'siparis-yonetimi',
    title: 'Sipariş Yönetimi',
    icon: 'ReceiptLong',
    articles: [
      {
        id: 'siparis-ekrani',
        title: 'Sipariş Ekranı ve Sekmeler',
        summary:
          'Ürün siparişleri, paket satışları, günlük girişler ve biletlerin listelendiği ekran.',
        roles: MANAGERS,
        feature: FeatureKey.ORDERS,
        videoUrl: 'https://youtu.be/FhCKCMB4Ssk',
        steps: [
          {
            text: 'Sol menüden "Siparişler"e tıklayın. Ekran dört sekmeye ayrılmıştır.'
          },
          {
            text: '"Ürün Siparişleri": Müşterilerin verdiği normal ürün/menü siparişleri. En sık kullanacağınız sekme budur.'
          },
          {
            text: '"Paket Satışları": Satılan etkinlik paketleri. "Günlük Girişler": Günlük giriş (giriş ücreti/hakkı) satışları. "Biletler": Bilet satışları.'
          },
          {
            text: 'Ürün Siparişleri tablosu şu sütunları gösterir: Müşteri, Konum, İşlem Yapan, Satın Alma Şekli, Sipariş Durumu, Sipariş Tutarı, Oluşturulma Tarihi ve İşlemler.'
          }
        ]
      },
      {
        id: 'siparis-durum-degistirme',
        title: 'Sipariş Durumunu Değiştirme',
        summary:
          'Bir siparişi Hazırlanıyor, Hazır, Teslim Edildi, İptal veya İade olarak işaretleme.',
        roles: MANAGERS,
        feature: FeatureKey.ORDERS,
        videoUrl: 'https://youtu.be/QTi2i_5LIY8',
        steps: [
          {
            text: 'Ürün Siparişleri tablosunda ilgili siparişin "İşlemler" sütununda "Durumu Düzenle" (kalem) simgesine tıklayın.'
          },
          {
            text: 'Açılan listeden yeni durumu seçin: Hazırlanıyor, Hazır, İptal Edildi, Teslim Edildi veya İade Edildi.'
          },
          {
            text: 'Değişikliği onaylamak için "Kaydet" (onay) simgesine, vazgeçmek için "İptal" simgesine tıklayın.',
            note: 'Durum güncellendiğinde bir onay mesajı görürsünüz. Sipariş durumları mutfak/servis akışını ve raporları etkiler.'
          },
          {
            text: 'Durum değişikliklerinin OTOMATİK ETKİLERİNİ bilin — sistem arka planda şunları yapar:',
            warning:
              '"İptal Edildi": Stok takipli ürünlerin stoğu otomatik geri yüklenir. "İade Edildi": Cüzdan veya kartla ödenen tutar müşterinin CÜZDAN BAKİYESİNE iade edilir (nakit olarak değil) ve muhasebe defterine iade kaydı düşer; puanla ödenen siparişte harcanan puanlar müşteriye geri verilir. Nakit siparişlerde otomatik para iadesi YAPILMAZ — parayı kasadan elden iade etmeniz gerekir.'
          }
        ]
      },
      {
        id: 'siparis-filtreleme',
        title: 'Siparişleri Filtreleme ve Arama',
        summary:
          'Satın alma şekli, durum, tarih ve lokasyona göre siparişleri süzme.',
        roles: MANAGERS,
        feature: FeatureKey.ORDERS,
        videoUrl: 'https://youtu.be/NH0HwZJMgns',
        steps: [
          {
            text: 'Tablonun üstündeki arama kutusuna müşteri veya sipariş bilgisini yazarak arayın.'
          },
          {
            text: 'Filtrelerle daraltın: Satın Alma Şekli (nakit, kart vb.), Sipariş Durumu, tarih aralığı ve Konum (şube).'
          },
          {
            text: 'Belirli bir günün veya durumun siparişlerini incelemek için filtreleri birlikte kullanın (örn. "bugün + Hazır").'
          }
        ]
      },
      {
        id: 'siparis-detay',
        title: 'Sipariş Detayını Görüntüleme',
        summary:
          'Bir siparişin ürünlerini, tutarını, harcanan puanı ve ödeme bilgisini görme.',
        roles: MANAGERS,
        feature: FeatureKey.ORDERS,
        videoUrl: 'https://youtu.be/yPvnlulq4hk',
        steps: [
          {
            text: 'İlgili siparişin "İşlemler" sütununda "Detayları Görüntüle" simgesine tıklayın.'
          },
          {
            text: 'Üst kısımda özet bilgiler: Sipariş ID, Sipariş Tarihi, Müşteri, Satın Alma Şekli, Şube, Sipariş Durumu, Sipariş Tutarı. Varsa "Harcanan Puan" ve "Bakiyeden Kesilen (Ekstra)" da gösterilir.'
          },
          {
            text: '"Ürünler" tablosu her kalemi Adet, Fiyat ve Toplam ile listeler.'
          },
          {
            text: '"Ödeme Detayı" bölümü Ödeme Türü, Tutar, alan kişi ve ödeme saatini gösterir.'
          }
        ]
      },
      {
        id: 'zayi-siparisleri',
        title: 'Zayi Siparişlerini Tanıma',
        summary:
          'Personel yemeği, ikram gibi 0 TL siparişlerin "Zayi" etiketiyle ayırt edilmesi.',
        roles: MANAGERS,
        feature: FeatureKey.ORDERS,
        videoUrl: 'https://youtu.be/pHaWJhtinvI',
        steps: [
          {
            text: 'Ürün Siparişleri tablosunda bazı satırlarda "Zayi" etiketi (chip) görürsünüz.'
          },
          {
            text: 'Bunlar 0 TL tutarındaki iç tüketim/zayi kayıtlarıdır (personel yemeği, ikram, fire, SKT imha) ve ciroya dahil edilmez.',
            note: 'Zayi kayıtlarının toplu analizi için Ana Pano\'daki "Zayi / İç Tüketim" raporunu kullanın (bkz. Ana Pano & Raporlar bölümü).'
          }
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ⑥ MASA YÖNETİMİ
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'masa-yonetimi',
    title: 'Masa Yönetimi',
    icon: 'TableRestaurant',
    articles: [
      {
        id: 'masa-genel',
        title: 'Masa Yönetimine Genel Bakış',
        summary:
          'Kat planları ve mutfak istasyonları (KDS) ile masa düzeninizi kurma.',
        roles: MANAGERS,
        feature: FeatureKey.TABLE_MANAGEMENT,
        videoUrl: 'https://youtu.be/yYjgVcacQ7I',
        steps: [
          {
            text: 'Sol menüden "Masa Yönetimi"ne girin. Ekran iki bölümden oluşur: "Kat Planları" ve "Mutfak İstasyonları (KDS)".'
          },
          {
            text: 'Önce bir kat planı oluşturursunuz (salonun ızgara düzeni), sonra bu plan üzerine masaları yerleştirirsiniz.'
          },
          {
            text: 'Bir kat planına tıklayınca o plana ait masalar açılır ve masaları buradan yönetirsiniz.'
          },
          {
            text: 'Masaların plan üzerindeki oturma düzeni (yerleşim) satış (POS) ekranından çizilir: POS > Masalar ekranında "Düzenle" düğmesi Düzenleme Modu\'nu açar; masaları sürükleyerek yerleştirir, "Kaydet" ile düzeni kaydedersiniz.',
            note: 'Admin panel kat planı, masa ve KDS tanımlarını yönetir; görsel düzen çizimi POS tarafında yapılır.'
          }
        ]
      },
      {
        id: 'kat-plani-olusturma',
        title: 'Kat Planı Oluşturma',
        summary: 'Salonu temsil eden ızgara düzenini (satır/sütun) tanımlama.',
        roles: MANAGERS,
        feature: FeatureKey.TABLE_MANAGEMENT,
        videoUrl: 'https://youtu.be/1Jyuf2iGj2Y',
        steps: [
          {
            text: 'Masa Yönetimi\'nde "Kat Planı Oluştur" düğmesine tıklayın.'
          },
          {
            text: 'Alanları doldurun: "Kat Planı Adı" (örn. Zemin Kat, Teras), "Izgara Satır Sayısı" ve "Izgara Sütun Sayısı" (salonun boyutu), "Sıralama".'
          },
          {
            text: '"Aktif" anahtarını açıp kaydedin. Izgara boyutu, masaları yerleştireceğiniz görsel düzeni belirler.'
          }
        ]
      },
      {
        id: 'masa-olusturma',
        title: 'Masa Oluşturma ve Yerleştirme',
        summary:
          'Kat planına masa ekleme; şekil, kapasite ve ızgara üzerinde konum seçme.',
        roles: MANAGERS,
        feature: FeatureKey.TABLE_MANAGEMENT,
        videoUrl: 'https://youtu.be/E60aGQmw6nw',
        steps: [
          {
            text: 'Bir kat planını açın ve "Masa Oluştur" düğmesine tıklayın.'
          },
          {
            text: 'Alanları doldurun: "Kat Planı", "Masa Adı", "Şekil" (Dikdörtgen, Daire veya Kare) ve "Kapasite" (kişi sayısı).'
          },
          {
            text: 'Görsel ızgara üzerinde masanın konumunu seçin (Satır/Sütun). Boş bir hücre seçin; dolu hücrelere ikinci masa yerleştirilemez.',
            note: 'Masaların yerleşimini sonradan değiştirmek için POS > Masalar ekranındaki "Düzenle" (Düzenleme Modu) ile masaları sürükleyip "Kaydet" demeniz yeterlidir.'
          },
          {
            text: '"Aktif" anahtarını ayarlayıp kaydedin. Masa artık kat planında görünür.'
          }
        ]
      },
      {
        id: 'masa-durum-bloklama',
        title: 'Masa Durumları ve Bloklama',
        summary:
          'Masaların durumlarını görme ve bir masayı geçici olarak kullanıma kapatma.',
        roles: MANAGERS,
        feature: FeatureKey.TABLE_MANAGEMENT,
        videoUrl: 'https://youtu.be/-Sc3U1T88Q4',
        steps: [
          {
            text: 'Masalar tablosu her masanın Masa Adı, Masa Numarası, Şekil, Kapasite ve Durumunu gösterir.'
          },
          {
            text: 'Masa durumları: Müsait, Dolu, Rezerve, Temizleniyor ve Bloke.'
          },
          {
            text: 'Bir masayı geçici olarak kullanıma kapatmak için "Masayı Bloke Et"; tekrar açmak için "Blokeyi Kaldır" düğmesini kullanın.',
            note: 'Bloke masaya POS üzerinden yeni sipariş/açılış yapılamaz; tadilat, arıza gibi durumlar için kullanışlıdır.'
          },
          {
            text: 'Masayı düzenleyebilir veya silebilirsiniz.'
          }
        ]
      },
      {
        id: 'kds-istasyonlari',
        title: 'Mutfak İstasyonu (KDS) Tanımlama',
        summary:
          'Hangi ürün kategorisinin hangi mutfak ekranına düşeceğini belirleme.',
        roles: MANAGERS,
        feature: FeatureKey.TABLE_MANAGEMENT,
        videoUrl: 'https://youtu.be/n8eA97PE29c',
        steps: [
          {
            text: 'Masa Yönetimi\'nde "Mutfak İstasyonları (KDS)" bölümünde "İstasyon Oluştur" düğmesine tıklayın.'
          },
          {
            text: 'Alanları doldurun: "İstasyon Adı" (örn. Sıcak Mutfak, Bar), "Kategoriler" (bu istasyona düşecek ürün kategorileri — çoklu seçim) ve "POS\'ta Görünüm Sırası".'
          },
          {
            text: '"Aktif" anahtarını açıp kaydedin. Böylece o kategorilerdeki siparişler ilgili mutfak istasyonuna yönlendirilir.'
          },
          {
            text: 'Buradan yalnızca istasyonları TANIMLARSINIZ.',
            warning:
              'Canlı mutfak/KDS ekranı (siparişlerin akışı) yönetim panelinde değil, satış (POS/masaüstü) uygulamasında çalışır. Bu sayfa yalnızca istasyon ve kategori eşleşmesini yapılandırır.'
          }
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ⑦ KULLANICI / PERSONEL YÖNETİMİ
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'kullanici-yonetimi',
    title: 'Kullanıcı / Personel Yönetimi',
    icon: 'ManageAccounts',
    articles: [
      {
        id: 'kullanici-listesi',
        title: 'Kullanıcı Listesi ve Filtreleme',
        summary:
          'Personel ve müşterileri listeleme, role/duruma göre süzme, toplu işlemler.',
        roles: MANAGERS,
        feature: FeatureKey.USERS,
        videoUrl: 'https://youtu.be/8RWbGzZ6FrA',
        steps: [
          {
            text: 'Sol menüden "Kullanıcı Yönetimi"ne tıklayın.'
          },
          {
            text: 'Tablo sütunları: İsim, Konum, Durum, Rol, Oluşturulma Tarihi ve Güncellenme Tarihi.'
          },
          {
            text: 'Filtreler: Durum, Rol, tarih aralığı ve Konum (şube). Arama kutusuyla ada göre arayın.'
          },
          {
            text: 'Toplu işlem: Birden fazla kullanıcı seçip topluca silebilir veya durumlarını güncelleyebilirsiniz.',
            note: 'Şube yöneticisi olarak kendi şubenizdeki alt roldeki kullanıcıları ve şirketinizin müşterilerini görürsünüz.'
          }
        ]
      },
      {
        id: 'kullanici-ekleme-otp',
        title: 'Yeni Kullanıcı Ekleme (OTP Doğrulamalı)',
        summary:
          'Personel/kullanıcı ekleme; telefon numarasının SMS koduyla doğrulanması.',
        roles: MANAGERS,
        feature: FeatureKey.USERS,
        videoUrl: 'https://youtu.be/ssoGW-k8_1c',
        steps: [
          {
            text: 'Kullanıcı Yönetimi ekranında "Yeni Kullanıcı" düğmesine tıklayın.'
          },
          {
            text: 'Alanları doldurun: "İsim", "Soyisim", "E-posta", "Telefon", "Şifre", "Rol", "Durum" ve (yetkinizdeyse) "Şirket" / "Şube".',
            note: 'Rol olarak yalnızca kendinizden düşük rolleri (örn. Çalışan) atayabilirsiniz.'
          },
          {
            text: 'Kaydetmeden önce telefon doğrulaması gerekir: "Doğrulama Kodu Gönder"e tıklayın. Kullanıcının telefonuna 6 haneli bir SMS kodu gider.'
          },
          {
            text: 'Gelen kodu "Doğrulama Kodu" alanına girip "Doğrula"ya tıklayın. Kod gelmezse "Kodu Tekrar Gönder"i kullanın.',
            warning:
              'Telefon doğrulanmadan kullanıcı oluşturulamaz. Numaranın doğru ve erişilebilir olduğundan emin olun.'
          },
          {
            text: 'Doğrulama tamamlanınca kullanıcıyı kaydedin.'
          }
        ]
      },
      {
        id: 'kullanici-detay',
        title: 'Kullanıcı Detayı ve Cüzdan',
        summary:
          'Bir kullanıcının bakiye, puan, sipariş ve aktivite geçmişini görme.',
        roles: MANAGERS,
        feature: FeatureKey.USERS,
        videoUrl: 'https://youtu.be/9vaITWU0c4Q',
        steps: [
          {
            text: 'Kullanıcı listesinde bir satırın "Detayları Görüntüle" simgesine tıklayın.'
          },
          {
            text: 'Üstte profil bilgileri (E-posta, Telefon, Şube, Şirket) ve cüzdan özeti: Bakiye, Puan, Giriş Serisi, Ücretsiz hak sayısı ve Son Giriş.'
          },
          {
            text: 'Sekmeler: Cüzdan Geçmişi, Siparişler, Aktiviteler, Kampanya Kullanımları ve (personel için) Çalışan İşlemleri.'
          },
          {
            text: 'Buradan kullanıcının şubelerini yönetmek için "Kullanıcı Şubelerini Yönet"e geçebilirsiniz (sonraki konu).'
          }
        ]
      },
      {
        id: 'kullanici-sube-atama',
        title: 'Kullanıcının Şubelerini Yönetme',
        summary:
          'Bir kullanıcıyı bir veya birden çok şubeye atama ve ana şube belirleme.',
        roles: MANAGERS,
        feature: FeatureKey.USERS,
        videoUrl: '',
        steps: [
          {
            text: 'Kullanıcı detayında "Kullanıcı Şubelerini Yönet"i açın. Atanmış şubeler tablosu Şube Adı, Mapcode, Durum ve Ana Şube bilgisini gösterir.'
          },
          {
            text: '"Şube Ekle" ile listeden bir şube seçip kullanıcıya atayın.'
          },
          {
            text: 'Bir şubeyi "Ana Şube" yapmak için ilgili satırda ana şube olarak işaretleyin. Gereksiz atamayı "Şubeyi Kaldır" ile silin.',
            note: 'Kullanıcının en az bir şubesi kalmalıdır; son şube kaldırılamaz.'
          }
        ]
      },
      {
        id: 'sifre-degistirme-guvenlik',
        title: 'Şifre Değiştirme (Güvenlik)',
        summary:
          'Kendi hesabınızın şifresini telefon doğrulamasıyla güncelleme.',
        roles: ALL_STAFF,
        videoUrl: 'https://youtu.be/Wod2FCFFK6g',
        steps: [
          {
            text: 'Sağ üstteki profil menüsünden hesap ayarlarına gidin ve "Güvenlik" sekmesini açın.'
          },
          {
            text: '"Şifre Değiştir"e tıklayın. Açılan pencerede "Telefon" numaranızı girip "Doğrulama Kodu Gönder"e basın.'
          },
          {
            text: 'Telefonunuza gelen "Doğrulama Kodu"nu girin, ardından "Yeni Şifre" ve "Yeni Şifre (Tekrar)" alanlarını doldurup kaydedin.',
            warning:
              'Şifreniz belirli bir uzunlukta olmalı ve iki alan birbirinin aynısı olmalıdır; aksi halde kaydedilmez.'
          }
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ⑧ ÜYELİK YÖNETİMİ
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'uyelik-yonetimi',
    title: 'Üyelik Yönetimi',
    icon: 'CardMembership',
    articles: [
      {
        id: 'uyelik-genel',
        title: 'Üyelik Ekranı',
        summary:
          'Üyelikler ve üyelik işlemleri (geçmiş) sekmelerine genel bakış.',
        roles: MANAGERS,
        feature: FeatureKey.MEMBERSHIPS,
        videoUrl: 'https://youtu.be/hq4LyL-dXBM',
        steps: [
          {
            text: 'Sol menüden "Üyelik Yönetimi > Üyelikler"e girin. Ekran iki sekmelidir.'
          },
          {
            text: '"Üyelikler": Aktif/geçmiş üyelik kayıtlarının listesi. "Üyelik İşlemleri": Satış, uzatma ve iade gibi üyelik hareketlerinin dökümü.'
          },
          {
            text: 'Yeni üyelik oluşturabilir, mevcut üyeliği düzenleyebilir/silebilir veya uzatabilirsiniz (sonraki konular).'
          }
        ]
      },
      {
        id: 'uyelik-olusturma',
        title: 'Yeni Üyelik Oluşturma',
        summary: 'Bir müşteriye plan seçerek üyelik tanımlama.',
        roles: MANAGERS,
        feature: FeatureKey.MEMBERSHIPS,
        videoUrl: 'https://youtu.be/tYseiWtKc5c',
        steps: [
          {
            text: 'Üyelikler sekmesinde yeni üyelik oluşturmayı başlatın ve müşteriyi seçin.'
          },
          {
            text: '(Yetkinizdeyse) "Şirket" ve "Şube", ardından "Ödeme Türü" ile üyelik planını seçin.',
            note: 'Üyelik planı listesi seçili şubeye göre gelir. Şirket yöneticisiyseniz üst bardan bir şube seçtiğinizde (veya formdaki şube alanından) plan seçimi aktifleşir; o şubede aktif plan yoksa önce "Üyelik Planları"ndan plan tanımlayın.'
          },
          {
            text: '"Başlangıç Tarihi", "Bitiş Tarihi" ve "Kalan Gün"ü girin.',
            note: 'Kullanım dönemini net belirtin; örneğin 30 günlük kullanım için tarih aralığı buna göre ayarlanmalıdır.'
          },
          {
            text: 'Bilgileri kontrol edip üyeliği kaydedin.'
          }
        ]
      },
      {
        id: 'uyelik-uzatma',
        title: 'Üyelik Uzatma',
        summary: 'Mevcut bir üyeliğin süresini yeni bir planla uzatma.',
        roles: MANAGERS,
        feature: FeatureKey.MEMBERSHIPS,
        videoUrl: 'https://youtu.be/wwx140jfmyw',
        steps: [
          {
            text: 'İlgili üyelik satırında "Üyeliği Uzat" işlemini açın.'
          },
          {
            text: '"Ödeme Türü"nü seçin ve uzatma için bir üyelik planı belirleyin. Seçtiğinizde planın "Plan Fiyatı" gösterilir.'
          },
          {
            text: '"Üyeliği Uzat" ile onaylayın. İşlem, üyelik işlemleri geçmişine kaydedilir.',
            note: 'ÖNEMLİ KURAL: Uzatma yeni bir üyelik kaydı açmaz; mevcut üyeliğin bitiş tarihini ileri taşır. Satış raporları her satışı/uzatmayı "Üyelik İşlemleri" sekmesindeki hareketlerden okur; iadeler orada negatif satır olarak görünür ve cirodan düşer.'
          }
        ]
      },
      {
        id: 'uyelik-planlari',
        title: 'Üyelik Planları',
        summary: 'Süre, geçerlilik ve fiyat içeren üyelik planları oluşturma.',
        roles: MANAGERS,
        feature: FeatureKey.MEMBERSHIPS,
        videoUrl: 'https://youtu.be/mLb8KicKBaI',
        steps: [
          {
            text: 'Sol menüden "Üyelik Yönetimi > Üyelik Planları"na girin. Tablo; Plan Adı, Süre, Geçerlilik, Fiyat, Durum ve aktif üyelik sayısını gösterir.'
          },
          {
            text: 'Yeni plan ekleyin: (yetkinizdeyse) "Şirket" / "Şube", "Plan Adı", "Süre (Gün)" (kullanım süresi), "Geçerlilik (Gün)" ve "Fiyat".'
          },
          {
            text: '"Durum"u (Aktif/Pasif) seçip kaydedin. Ekranda "müşteri bu üyeliği kaç gün kullanabilir" gibi açıklamalar gösterilir.'
          }
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ⑨ SADAKAT & KUPONLAR
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'sadakat-kuponlar',
    title: 'Sadakat & Kuponlar',
    icon: 'Loyalty',
    articles: [
      {
        id: 'sadakat-genel',
        title: 'Sadakat & Kuponlar Genel Bakış',
        summary: 'Ürün puanları, kupon şablonları ve damga kartlarına giriş.',
        roles: MANAGERS,
        feature: FeatureKey.MOBILE_LOYALTY,
        videoUrl: 'https://youtu.be/xSLXurXjgMc',
        steps: [
          {
            text: 'Sol menüden "Sadakat & Kuponlar" grubunu açın. Alt öğeler: Ürün Puanları, Kupon Şablonları ve Damga Kartı.'
          },
          {
            text: 'Bu modül, müşteri sadakatini artırmak için puan kazanma/harcama, indirim kuponları ve damga (sadakat kartı) kampanyalarını yönetmenizi sağlar.',
            note: 'Sadakat Ayarları (puan oranları gibi şirket geneli ayarlar) genellikle şirket yöneticisi tarafından belirlenir ve şube yöneticisi menüsünde görünmez.'
          }
        ]
      },
      {
        id: 'urun-puanlari',
        title: 'Ürün Puanları (Puanla Alınabilir Ürünler)',
        summary:
          'Müşterilerin biriktirdiği puanla alabileceği ürünleri ve puan maliyetini belirleme.',
        roles: MANAGERS,
        feature: FeatureKey.MOBILE_LOYALTY,
        videoUrl: 'https://youtu.be/sWEpf_Zmirs',
        steps: [
          {
            text: 'Sol menüden "Sadakat & Kuponlar > Ürün Puanları"na girin.'
          },
          {
            text: '"Ürüne Puan Maliyeti Ekle" ile bir ürün seçin ("Ürün Seçin") ve o ürünün "Puan Maliyeti"ni (kaç puana alınabileceğini) girin.'
          },
          {
            text: 'Kaydedince ürün, müşterilerin puanlarıyla alabileceği ürünler arasında yer alır.'
          }
        ]
      },
      {
        id: 'kupon-sablonlari',
        title: 'Kupon Şablonları',
        summary:
          'Belirli koşullarda müşteriye indirim/ödül veren kupon şablonları oluşturma.',
        roles: MANAGERS,
        feature: FeatureKey.MOBILE_LOYALTY,
        videoUrl: 'https://youtu.be/WijBxdGk7LM',
        steps: [
          {
            text: 'Sol menüden "Sadakat & Kuponlar > Kupon Şablonları"na girin ve yeni şablon oluşturun.'
          },
          {
            text: 'ŞUBE/ŞİRKET: Şube yöneticisiyseniz şube seçici görmezsiniz; kendi şubeniz otomatik seçili ve pasif (disabled) gelir — kupon otomatik olarak sizin şubeniz için oluşturulur. Şirket yöneticisi/süper admin şube (süper admin ayrıca şirket) seçer.',
            note: 'Normal kuponlar yalnızca seçilen şubede geçerlidir. "Tüm şubelerde geçerli" kupon yalnızca damga kartı ödülü olarak sistem tarafından otomatik üretilir; buradan tüm-şube kuponu oluşturulmaz.'
          },
          {
            text: 'Temel bilgi: "İsim" ve "Açıklama"yı girin.'
          },
          {
            text: 'TETİKLEME KOŞULU: "Tetikleyici Ürün" ve "Minimum Adet" ile kuponun hangi alışverişte kazanılacağını belirleyin (örn. Filtre Kahve, min 5 adet).',
            warning:
              'Koşul yalnızca "tetikleyici ürün + minimum ADET" üzerinedir. MİNİMUM SEPET TUTARI (örn. "100 TL üzeri alışverişte") gibi bir koşul YOKTUR; müşteriye bu şekilde söz vermeyin.'
          },
          {
            text: 'ÖDÜL TİPİ üçe ayrılır: (1) Yüzde İndirim — "İndirim Yüzdesi (%)" (1-100). (2) Sabit Tutar İndirim — "İndirim Tutarı (TL)". (3) Bedava Ürün — "Ödül Grupları" ile hangi üründen kaç adet verileceğini tanımlarsınız.'
          },
          {
            text: 'ÖDÜL GRUPLARI (bedava ürün ödülünde): Birden çok grup ekleyip her gruba ürün ve "seçilebilecek adet" tanımlayarak "menüden seç" tarzı ödüller kurabilirsiniz (örn. 1 içecek + 1 tatlı).'
          },
          {
            text: 'KURALLAR: "Geçerlilik Süresi (Gün)", "Başlangıç/Bitiş Tarihi", "Kullanıcı Başına Limit" ve "Toplam Kullanım Limiti" ile kuponun ömrünü ve kaç kez kullanılabileceğini sınırlayın.'
          }
        ],
        tips: [
          'ÖRNEK 1 — % indirim: Ödül Tipi = Yüzde İndirim, %15; Kullanıcı Başına Limit = 1; Geçerlilik = 30 gün.',
          'ÖRNEK 2 — Sabit TL indirim: Ödül Tipi = Sabit Tutar, 50 ₺ indirim.',
          'ÖRNEK 3 — X al, hediye kazan: Tetikleyici Ürün = Filtre Kahve, Minimum Adet = 5; Ödül Tipi = Bedava Ürün (ödül grubu: 1 Filtre Kahve). Yani "5 kahve alana 1 kahve bedava".',
          'ÖRNEK 4 — Menüden seç ödülü: Ödül Tipi = Bedava Ürün; Grup 1 = İçecekler (1 adet), Grup 2 = Tatlılar (1 adet). Müşteri her gruptan birini seçer.',
          'DAĞITIM: Kupon şablonundan tekli veya toplu PROMOSYON KODU üretebilir (kullanıcı başına limit ve toplu etiket ile) ve kodları CSV olarak dışa aktarabilirsiniz.'
        ]
      },
      {
        id: 'damga-kartlari',
        title: 'Damga Kartları',
        summary:
          'Belirli sayıda ürün alımında otomatik bedava ürün kazandıran damga (sadakat kartı) kampanyaları.',
        roles: MANAGERS,
        feature: FeatureKey.MOBILE_LOYALTY,
        videoUrl: 'https://youtu.be/UPOz2owN2rg',
        steps: [
          {
            text: 'NASIL ÇALIŞIR: Damga kartı ŞİRKET SEVİYESİNDE tek bir sadakat kampanyasıdır (şirketin aynı anda yalnızca 1 aktif damga kartı olabilir). Müşteri, kampanyaya dahil ürünlerden aldıkça damga biriktirir.',
            note: 'ÖNEMLİ: Damga, harcama tutarına veya ziyaret sayısına göre değil, kampanyaya dahil ürünlerin SATIN ALINAN ADEDİNE göre kazanılır. Örneğin dahil bir üründen 3 adet alınması 3 damga kazandırır.'
          },
          {
            text: 'ÖDÜL: Müşteri "Gerekli Damga Sayısı"na ulaştığında sistem otomatik olarak bir BEDAVA ÜRÜN kuponu verir. Sayaç sıfırlanır, hedefi aşan damgalar bir sonraki döngüye devreder; böylece kampanya kesintisiz döner.',
            note: 'Kazanılan bedava ürün kuponu her zaman TÜM ŞUBELERDE geçerlidir — bunu sistem otomatik yönetir, ayrıca bir ayar yapmanıza gerek yoktur.'
          },
          {
            text: 'ŞİRKET YÖNETİCİSİ / SÜPER ADMİN: Sol menüden "Sadakat & Kuponlar > Damga Kartı"na girip "Damga Kartı Oluştur"a tıklayın. Alanlar: "Kampanya Adı", açıklama, "Gerekli Damga Sayısı" ve ödül kuponunun "Geçerlilik Süresi (Gün)". Kampanyayı yalnızca siz oluşturur/düzenler, tüm şubelerin katılan ürünlerini ve istatistiklerini görürsünüz.'
          },
          {
            text: 'Kampanyayı kaydettikten sonra her şube için "damga kazandıran ürünleri" tanımlayın. Yalnızca bu ürünlerin satın alınması damga kazandırır.'
          },
          {
            text: 'ŞUBE YÖNETİCİSİ: Bu ekranda kampanya ayarlarını (ad, gerekli damga, geçerlilik) ve istatistikleri GÖREMEZ; şube seçici de yoktur. Yalnızca KENDİ ŞUBENİZDE damga kazandıracak ürünleri seçebilirsiniz.',
            warning:
              'Damga kartı kampanyasını yalnızca şirket yöneticisi oluşturur. Şube yöneticisi olarak "tüm şubelerde geçerli olsun" gibi bir seçim yapmazsınız — ödülün tüm şubelerde geçerli olması sistem tarafından otomatik sağlanır. Sizin göreviniz yalnızca şubenizin katılan ürünlerini belirlemektir.'
          }
        ],
        tips: [
          'Yeni bir aktif kampanya başlatmadan önce mevcut aktif damga kartını pasife almanız gerekir (aynı anda tek aktif kampanya kuralı).',
          'Hangi ürünlerin damga kazandıracağını iyi seçin: çok satan bir ürünü dahil ederseniz hedefe hızlı ulaşılır, niş bir ürünü dahil ederseniz o ürünün satışını teşvik edersiniz.'
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ⑩ KAMPANYA YÖNETİMİ
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'kampanya-yonetimi',
    title: 'Kampanya Yönetimi',
    icon: 'LocalOffer',
    articles: [
      {
        id: 'kampanya-listesi',
        title: 'Kampanya Listesi',
        summary:
          'İndirim ve kombo fiyat kampanyalarınızı listeleme, filtreleme ve yönetme.',
        roles: MANAGERS,
        feature: FeatureKey.CAMPAIGNS,
        videoUrl: 'https://youtu.be/lnSZbjeKoL0',
        steps: [
          {
            text: 'Sol menüden "Kampanya Yönetimi"ne tıklayın.'
          },
          {
            text: 'Tablo sütunları: Görsel, Başlık, Konum, Durum, Kampanya Türü, İndirim Yüzdesi, Başlangıç ve Bitiş Tarihi.'
          },
          {
            text: 'Filtreler: Durum, Kampanya Türü, tarih aralığı ve Konum. Kampanyayı düzenleyebilir, detayına/kullanımlarına gidebilir veya silebilirsiniz.'
          },
          {
            text: 'Etkinlik paketleri (müşterinin satın alıp hakkını kullandığı paketler) bu listede yer almaz; sol menüdeki "Paketler" sayfasında yönetilir.'
          }
        ]
      },
      {
        id: 'kampanya-olusturma',
        title: 'Kampanya Oluşturma',
        summary:
          'İndirim veya kombo fiyat kampanyası tanımlama; tüm form alanları.',
        roles: MANAGERS,
        feature: FeatureKey.CAMPAIGNS,
        videoUrl: 'https://youtu.be/tbs4WhTWIs4',
        steps: [
          {
            text: 'Kampanya Yönetimi ekranında "Yeni Kampanya" düğmesine tıklayın.'
          },
          {
            text: '"Başlık" ve "Açıklama"yı girin, ardından "Kampanya Türü"nü seçin ve "Durum"u (Aktif/Pasif) belirleyin.'
          },
          {
            text: 'İndirim kampanyasında "İndirim Yüzdesi"ni girin. "Kombo Fiyat" türünde, kampanyaya eklenen ürünlerin hepsi sepetteyse uygulanacak "Paket Fiyatı"nı girin.'
          },
          {
            text: 'Kapsamı belirleyin: (yetkinizdeyse) "Şirket" / "Şube" ve gerekiyorsa "Kategori". Son olarak "Başlangıç Tarihi" ve "Bitiş Tarihi"ni seçip kaydedin.'
          }
        ]
      },
      {
        id: 'paketler',
        title: 'Paketler (Etkinlik Paketleri)',
        summary:
          'Müşterinin satın alıp hakkını kullandığı etkinlik paketlerini tanımlama ve listeleme.',
        roles: MANAGERS,
        feature: FeatureKey.CAMPAIGNS,
        videoUrl: '',
        steps: [
          {
            text: 'Sol menüden "Paketler"e tıklayın. Tablo; Görsel, Başlık, Konum, Durum, Paket Fiyatı, Paket Kullanım Hakkı, Başlangıç ve Bitiş Tarihi sütunlarını gösterir.'
          },
          {
            text: '"Yeni Paket" ile "Başlık", "Açıklama", "Paket Fiyatı", "Paket Kullanım Hakkı" (kaç sınava/etkinliğe girileceği) ve KDV oranını girin; tarihleri seçip kaydedin. Paketin türü sabittir, indirim yüzdesi sorulmaz.'
          },
          {
            text: 'Paketin detayından pakete dahil etkinlikleri ekleyin. Kasada paketler "Sınavlar & Paketler" sekmesinden satılır.'
          }
        ]
      },
      {
        id: 'kampanya-kullanimlari',
        title: 'Kampanya Kullanımları ve İade',
        summary:
          'Bir kampanyanın kimler tarafından kullanıldığını görme ve paket iadesi.',
        roles: MANAGERS,
        feature: FeatureKey.CAMPAIGNS,
        videoUrl: 'https://youtu.be/VzvDmr6f3q4',
        steps: [
          {
            text: 'Kampanya listesinde ilgili kampanyanın "Kampanya Kullanımları" işlemine gidin.'
          },
          {
            text: 'Tablo; Müşteri, kalan/toplam kullanım hakkı ve tarih bilgisini gösterir.'
          },
          {
            text: 'Bir paket kullanımını iade etmek için ilgili satırdaki "İade" işlemini kullanın.',
            note: 'İade işlemi kampanya hakkını ve ilgili bakiyeyi geri alır; dikkatli kullanın.'
          }
        ]
      },
      {
        id: 'toplu-musteri-verisi',
        title: 'Toplu Müşteri Verisi Girişi',
        summary:
          'Bir etkinlik paketi için birden çok müşteriye toplu hak tanımlama.',
        roles: MANAGERS,
        feature: FeatureKey.CAMPAIGNS,
        videoUrl: '',
        steps: [
          {
            text: '"Paketler" sayfasında "Toplu Müşteri Verisi Ekleme" düğmesine tıklayın.'
          },
          {
            text: '"Yeni Satır Ekle" ile müşteri satırlarını çoğaltıp bilgileri girin.'
          },
          {
            text: 'Tümünü doldurduktan sonra kaydedin; sistem başarı/hata durumunu bildirir.'
          }
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ⑪ BİLDİRİM YÖNETİMİ
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'bildirim-yonetimi',
    title: 'Bildirim Yönetimi',
    icon: 'NotificationsActive',
    articles: [
      {
        id: 'bildirim-genel',
        title: 'Bildirim (Push) Kampanyaları',
        summary:
          'Müşterilere duyuru, hatırlatma ve fırsat bildirimleri gönderme.',
        roles: MANAGERS,
        feature: FeatureKey.NOTIFICATIONS,
        videoUrl: 'https://youtu.be/td4KTt1wxHA',
        steps: [
          {
            text: 'Sol menüden "Bildirim Yönetimi"ne tıklayın. Gönderilmiş/planlanmış bildirim kampanyaları listelenir.'
          },
          {
            text: 'Bu modülle müşterilerinize bilgilendirme, duyuru, hatırlatma veya özel fırsat bildirimleri (push) gönderebilirsiniz.',
            note: 'Bildirim menüsü iki alt bölümden oluşur: "Bildirim Kampanyaları" (gönderilmiş/planlanmış bildirimler) ve "Bildirim Şablonları" (tekrar kullanılabilir metinler).'
          },
          {
            text: 'ŞUBE YÖNETİCİSİ KAPSAMI: Şube yöneticisi olarak bildirimleri yalnızca KENDİ ŞUBENİZİN müşterilerine gönderebilirsiniz.',
            warning:
              'Hedef kitlede "Tüm Kullanıcılar" seçseniz veya başka bir şube belirtseniz bile sistem, gönderimi otomatik olarak yalnızca kendi şubenizin müşterilerine daraltır. Şirket yöneticisi ise tüm şirketi veya seçtiği şubeleri hedefleyebilir.'
          },
          {
            text: '"Yeni Bildirim" düğmesiyle yeni bir bildirim hazırlayabilirsiniz (sonraki konu).'
          }
        ]
      },
      {
        id: 'bildirim-olusturma',
        title: 'Bildirim Oluşturma Sihirbazı (4 Adım)',
        summary:
          'İçerik, hedef kitle, zamanlama ve önizleme adımlarıyla bildirim gönderme.',
        roles: MANAGERS,
        feature: FeatureKey.NOTIFICATIONS,
        videoUrl: 'https://youtu.be/WqJYObtmCFc',
        steps: [
          {
            text: '1. ADIM — İçerik: (İsteğe bağlı) "Şablon Seç", ardından "Başlık" ve "İçerik" yazın. "Kullanılabilir değişkenler" (kullanıcı adı, şirket, şube) ekleyebilir; "Kategori", "Görsel URL" ve "Deep Link" belirtebilirsiniz.'
          },
          {
            text: '2. ADIM — Hedef Kitle: "Hedef Kitle Türü"nü seçin: Tüm Kullanıcılar, Segment veya Bireysel Seçim. Segmentte "Roller", "Şirketler", "Şubeler" ve "Sipariş Vermiş" / "Cüzdan Bakiyesi Var" filtrelerini kullanıp "Önizle" ile kaç kişiye gideceğini görün.',
            note: 'Şube yöneticisiyseniz seçtiğiniz hedef kitle ne olursa olsun bildirim yalnızca kendi şubenizin müşterilerine ulaşır; "Önizle" sayısı da bu kapsama göre hesaplanır.'
          },
          {
            text: '3. ADIM — Zamanlama: "Hemen Gönder" veya "Gönderim Zamanı" (ileri tarih/saat) seçin. Gerekirse "Son Geçerlilik Tarihi" belirleyin.'
          },
          {
            text: '4. ADIM — Önizleme: Özeti kontrol edip "Gönder"e tıklayın.',
            warning:
              'Bildirim gönderildikten sonra geri alınamaz. Hedef kitleyi ve içeriği göndermeden önce mutlaka kontrol edin.'
          }
        ]
      },
      {
        id: 'bildirim-sablonlari',
        title: 'Bildirim Şablonları',
        summary: 'Sık kullanılan bildirim metinlerini şablon olarak kaydetme.',
        roles: MANAGERS,
        feature: FeatureKey.NOTIFICATIONS,
        videoUrl: 'https://youtu.be/g1gjShFl8Xs',
        steps: [
          {
            text: 'Sol menüden "Bildirim Yönetimi > Bildirim Şablonları"na girin ve yeni şablon oluşturun.'
          },
          {
            text: 'Alanlar: "Şablon Adı", "Başlık", "İçerik", "Kategori", "Minimum Rol" (şablonu kullanabilecek en düşük rol), "Görsel URL" ve "Deep Link".'
          },
          {
            text: 'Kaydedilen şablonu, bildirim oluştururken 1. adımdaki "Şablon Seç" listesinden seçerek hızlıca kullanabilirsiniz.'
          }
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ⑫ AKTİVİTE YÖNETİMİ
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'aktivite-yonetimi',
    title: 'Etkinlik (Aktivite) Yönetimi',
    icon: 'EventNote',
    articles: [
      {
        id: 'aktivite-olusturma',
        title: 'Etkinlik Oluşturma ve Yönetme',
        summary:
          'Etkinlik/aktivite kayıtları tanımlama ve durumlarını yönetme.',
        roles: MANAGERS,
        feature: FeatureKey.ACTIVITIES,
        videoUrl: 'https://youtu.be/riq8pK4FHOQ',
        steps: [
          {
            text: 'Sol menüden "Etkinlik Yönetimi"ne tıklayın. Mevcut etkinlikler tablo halinde listelenir.'
          },
          {
            text: 'Yeni aktivite ekleyin: "Başlık", "Açıklama", "Durum", (yetkinizdeyse) "Şirket" / "Şube" ve görsel.'
          },
          {
            text: 'Bir aktiviteye tıklayarak detayına girebilir, alt aktiviteleri ve ilgili biletleri yönetebilirsiniz.'
          },
          {
            text: 'Bir seansı satın alanları görmek için seans kartındaki "Katılımcılar" butonuna tıklayın. Açılan listede ad, soyad, telefon veya e-posta ile arama yapabilir, bilet durumuna göre filtreleyebilirsiniz.'
          }
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ⑬ FİNANS
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'finans',
    title: 'Finans',
    icon: 'AccountBalanceWallet',
    articles: [
      {
        id: 'finans-ozet',
        title: 'Finansal Özet',
        summary:
          'Toplam kazanç, ödenen/ödenmeyen tutarlar ve sipariş sayısı özeti.',
        roles: MANAGERS,
        feature: FeatureKey.FINANCE,
        videoUrl: 'https://youtu.be/4FzuYLfDeH4',
        steps: [
          {
            text: 'Sol menüden "Finans"a girin. İlk sekme "Finansal Özet"tir.'
          },
          {
            text: 'Kartlar: "Toplam Kazanç", "Toplam Ödenen", "Toplam Ödenmeyen" ve "Sipariş Sayısı". Ayrıca ödeme durumu ve aylık/günlük özet gösterilir.',
            note: 'Burada yalnızca sistem üzerinden akan para gösterilir. Değerler seçili tarih aralığına göre hesaplanır.'
          },
          {
            text: 'Üstteki tarih aralığı seçiciyle dönemi değiştirebilirsiniz.'
          }
        ]
      },
      {
        id: 'para-cekme-talepleri',
        title: 'Para Çekme Talepleri',
        summary:
          'Çekilebilir bakiye için oluşturulan talepleri onaylama/reddetme.',
        roles: MANAGERS,
        feature: FeatureKey.FINANCE,
        videoUrl: 'https://youtu.be/1QD-gMQlydM',
        steps: [
          {
            text: 'Finans ekranında "Para Çekme Talepleri" sekmesine geçin. Tablo; Talep Eden, Tutar ve Durum bilgisini gösterir.'
          },
          {
            text: 'Bir talebi işleme almak için durumunu güncelleyin: Onayla, Reddet, Kısmen Ödendi olarak işaretle veya İptal Et.'
          },
          {
            text: 'İşlem öncesi "Durum Değişikliğini Onayla" penceresiyle onay verirsiniz. Durumlar: Beklemede, Onaylandı, Kısmen Ödendi, Reddedildi, İptal Edildi.',
            warning:
              'Para çekme durum değişiklikleri finansal işlemleri etkiler; talebi ve tutarı dikkatle kontrol edin.'
          }
        ]
      },
      {
        id: 'muhasebe-defteri',
        title: 'Muhasebe Defteri',
        summary:
          'Şube bakiyeleri ve hesap hareketlerini görme; manuel düzeltme.',
        roles: MANAGERS,
        feature: FeatureKey.FINANCE,
        videoUrl: 'https://youtu.be/SZhqE8m1B3c',
        steps: [
          {
            text: 'Finans ekranında "Muhasebe Defteri" sekmesine geçin. "Şube Bakiyeleri" listesi güncel bakiyeleri ve son işlemi gösterir.'
          },
          {
            text: 'Bir şubenin hareketlerini görüntüleyin; gerekiyorsa "Manuel Düzeltme" veya "Limit Ayarla" işlemlerini kullanın.',
            warning:
              'Manuel düzeltme ve limit ayarlama gibi işlemler yetki gerektirir; şube yöneticisi bu ekranı çoğunlukla görüntüleme amaçlı kullanır.'
          }
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ⑭ ŞUBE AYARLARI
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'sube-ayarlari',
    title: 'Şube Ayarları',
    icon: 'Storefront',
    articles: [
      {
        id: 'sube-ayarlarina-erisim',
        title: 'Şube Ayarlarına Erişim',
        summary:
          'Şube yöneticisi olarak şube detay ve ayarlarına nasıl ulaşırsınız.',
        roles: MANAGERS,
        videoUrl: 'https://youtu.be/k9RY92x3t_s',
        steps: [
          {
            text: 'Sağ üst köşedeki kullanıcı kutusuna (profil menüsü) tıklayın.'
          },
          {
            text: 'Açılan menüden "Şube Detayları"nı seçin. Şubenizin bilgi kartları ve altında yönetim panelleri (QR Menü, Günlük Giriş Türleri, Finansal Bilgiler, POS Ayarları, POS İşlemleri) açılır.',
            note: 'Şube yöneticisinde sol menüde "Şube Yönetimi" listesi görünmez; şube ayarlarına bu "Şube Detayları" bağlantısından ulaşırsınız. Aynı menüde "Lisans Detayları" ile aboneliğinizi de görebilirsiniz.'
          },
          {
            text: 'Yalnızca kendi şubenizin bilgilerini görür ve düzenlersiniz.'
          }
        ]
      },
      {
        id: 'qr-menu',
        title: 'QR Menü Oluşturma',
        summary:
          'Müşterilerin QR okutarak ürünleri gördüğü dijital menüyü oluşturma ve PDF indirme.',
        roles: MANAGERS,
        videoUrl: 'https://youtu.be/Tqx3vjLC7nM',
        steps: [
          {
            text: 'Şube Detayları\'nda "QR Menü" bölümüne gidin. Henüz oluşturulmadıysa "QR Menü Oluştur"a tıklayın.',
            note: 'ÖNEMLİ KURAL: QR menüde yalnızca O ŞUBEDE satışa açık ürünler listelenir — pasif ürünler ve şube özelinde satışa kapatılmış ürünler menüde görünmez. Ürünlerde tanımlı alerjen bilgileri de QR menüde müşteriye gösterilir.'
          },
          {
            text: 'Oluşturulunca QR görseli ve "Menü Bağlantısı" görünür. "Bağlantıyı kopyala" veya "Yeni sekmede aç" ile bağlantıyı kullanabilirsiniz.'
          },
          {
            text: 'Baskı için "QR Kodu PDF İndir" (masalara koymak üzere) veya "Menüyü PDF İndir" (menü listesi) düğmelerini kullanın.'
          },
          {
            text: 'Gerektiğinde QR menüyü yeniden oluşturabilir veya devre dışı bırakabilirsiniz.',
            warning:
              'QR menüyü yeniden oluşturmak eski bağlantıyı geçersiz kılabilir; basılı QR kodları güncellemeniz gerekebilir.'
          }
        ]
      },
      {
        id: 'gunluk-giris-turleri',
        title: 'Günlük Giriş Türleri',
        summary:
          'Şubeye özel giriş ücreti/hakkı türleri ve hediye kupon tanımlama.',
        roles: MANAGERS,
        feature: FeatureKey.DAILY_LOGINS,
        videoUrl: 'https://youtu.be/s2_oxOW7Mpg',
        steps: [
          {
            text: 'Şube Detayları\'nda "Günlük Giriş Türleri" bölümüne gidin ve "Yeni Giriş Türü Ekle"ye tıklayın.'
          },
          {
            text: 'Alanları doldurun: "İsim", "Açıklama", "Fiyat" ve (isteğe bağlı) "Hediye Kupon Şablonu".'
          },
          {
            text: '"Aktif" durumunu ayarlayıp kaydedin. Tanımlanan giriş türleri POS/satış tarafında kullanılır.'
          }
        ]
      },
      {
        id: 'finansal-bilgiler',
        title: 'Finansal Bilgiler (Fatura / IBAN)',
        summary:
          'Şubenin fatura ve ödeme bilgilerini (ünvan, vergi, IBAN) tanımlama.',
        roles: MANAGERS,
        feature: FeatureKey.FINANCE,
        videoUrl: 'https://youtu.be/mbT81vnT9A4',
        steps: [
          {
            text: 'Şube Detayları\'nda "Finansal Bilgiler" bölümüne gidin.'
          },
          {
            text: '"Kurum Türü"nü seçin (Şirket / Şahıs). Şirket için "Şirket Ünvanı", şahıs için "Ad Soyad" alanını doldurun.'
          },
          {
            text: '"Vergi/TC Kimlik No" ve "Vergi Dairesi" bilgilerini girin.'
          },
          {
            text: '"IBAN" alanına Türkiye IBAN\'ınızı (TR + 24 hane) girip kaydedin.',
            warning:
              'IBAN ve vergi bilgileri ödeme/fatura işlemlerinde kullanılır; doğruluğundan emin olun.'
          }
        ]
      },
      {
        id: 'pos-odeme-ayarlari',
        title: 'POS / Ödeme Ayarları',
        summary: 'Fiziksel POS sağlayıcısı ve ödeme cihazı tanımlama.',
        roles: MANAGERS,
        feature: FeatureKey.POS,
        videoUrl: '',
        steps: [
          {
            text: 'Şube Detayları\'nda "Fiziksel POS Ayarları" bölümüne gidin.'
          },
          {
            text: 'Sağlayıcı için: "Provider", "Görünen ad", "Varsayılan provider" ve sağlayıcının istediği "Credentials JSON" / "Settings JSON" alanlarını doldurun.'
          },
          {
            text: 'Cihaz kaydı için: "Şube provider", "Cihaz adı", "Terminal ID", "Seri no", "Bağlantı tipi", "Varsayılan cihaz" ve gerekiyorsa "Metadata JSON".',
            warning:
              'POS kimlik bilgileri (credentials) hassastır. Bu ayarlar genellikle kurulum sırasında teknik ekiple birlikte yapılır.'
          }
        ]
      },
      {
        id: 'pos-islemleri',
        title: 'POS İşlemleri ve İade',
        summary: 'Günlük POS işlemlerini görüntüleme, iade ve mutabakat.',
        roles: MANAGERS,
        feature: FeatureKey.POS,
        videoUrl: '',
        steps: [
          {
            text: 'Şube Detayları\'nda "POS Cihazı" sekmesini açın; "POS İşlemleri" bölümü cihaz ayarlarının altındadır. Gün sonu için "İş günü" seçip "Mutabakat Çalıştır" ile sonuçlanmamış işlemleri görebilirsiniz.'
          },
          {
            text: 'Pavo cihazındaki bir satışı iade etmek için satırdaki "İade Et" düğmesine basın, tutarı ve (isteğe bağlı) sebebi girin. İade cihaza gider; cihaz onayı bekleniyorsa sonuç birkaç dakika içinde listeye yansır.',
            note: 'İade cihazda reddedilirse ya da satış kaydı otomatik geri alınamazsa ekranda hata olarak gösterilir; bu durumda iade tamamlanmamıştır.'
          },
          {
            text: 'Beko (Token) ve InPOS cihazlarında ödenmiş satış cihazdan iade edilemez. Parayı cihaz dışında iade edin (kart ödemesini banka uygulamasından, nakdi kasadan), sonra satırdaki "Cihaz dışında iade edildi" düğmesine basın, gerekçeyi (en az 8 karakter) ve varsa banka iade numarasını girin. InPOS cihazında iade fişi bastıysanız ilgili kutuyu işaretleyin.',
            warning:
              'Kayıt satışın tamamı içindir ve geri alınamaz. Kayıt girilince sipariş iptal edilir, stok ve puan geri döner; giriş, bilet, paket, üyelik ve açık hesap alacağı geri alınır, masa adisyonu iptal olur. Beko fişi Z raporunda kalır; mali düzeltme için muhasebecinize başvurun.'
          },
          {
            text: 'Masa iadelerinde önce adisyonun kapanış ödemesini, sonra kısmi ödemeleri iade edin. Bölünmüş hesapta her bölme ayrı iade edilir; son bölme iade edilince adisyon iptal olur.'
          },
          {
            text: 'Açık hesap satışına tahsilat yapıldıysa önce tahsilat işleminin iade kaydını girin. Cihaz dışında alınmış bir tahsilat geri alınamaz; bu durumda satış iade kaydına alınamaz.'
          }
        ],
        tips: [
          'İade edilen satış raporlardan ilk satış tarihinde düşer; iade gününe ayrı bir eksi satır yazılmaz.',
          'Kasiyerler bu kaydı giremez; iade gerektiğinde şube yöneticisine başvururlar.'
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ⑮ YEMEK PLATFORMLARI ENTEGRASYONU
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'yemek-platformlari',
    title: 'Yemek Platformları',
    icon: 'DeliveryDining',
    articles: [
      {
        id: 'yemek-platformu-genel',
        title: 'Entegrasyon Ekleme ve Genel Bakış',
        summary:
          'Yemeksepeti, Trendyol GO ve Getir entegrasyonlarını tanımlama.',
        roles: MANAGERS,
        feature: FeatureKey.FOOD_DELIVERY,
        videoUrl: '',
        steps: [
          {
            text: 'Sol menüden "Yemek Platformları"na tıklayın. Ekran, "Yemek Platformu Entegrasyonları"nı yönetmenizi sağlar.'
          },
          {
            text: '"Yeni Entegrasyon" düğmesi bir kurulum sihirbazı açar. İlk adımda platformu seçin (Yemeksepeti, Trendyol GO veya Getir); sonraki adımlar seçtiğiniz platforma göre şekillenir.',
            note: 'Her platform farklı bilgi ister. Sihirbaz yalnız o platformun gerçekten kullandığı alanları gösterir, her alanın altında bilginin nereden alınacağı yazar.'
          },
          {
            text: 'Bağlantı bilgilerini girip kaydedin (örn. Trendyol GO için API Key + API Secret + Satıcı ID; Yemeksepeti için Client ID + Client Secret). Ardından sihirbaz mağazalarınızı platformdan çekip listeler — mağaza numarasını elle aramanız gerekmez.',
            warning:
              'API Key/Secret bilgileri hassastır ve platformun satıcı panelinden alınır. Entegrasyonu doğru şubeye eşlediğinizden emin olun.'
          },
          {
            text: 'Davranış ayarlarını belirleyip son adımda "Doğrula ve bitir" ile bağlantıyı sınayın. Başarılıysa entegrasyon Aktif duruma geçer.',
            note: 'Trendyol GO kurulumunun adım adım anlatımı için "Trendyol GO Kurulumu (Sıfırdan)" makalesine bakın.'
          }
        ]
      },
      {
        id: 'trendyol-go-kurulum',
        title: 'Trendyol GO Kurulumu (Sıfırdan)',
        summary:
          'API bilgilerini alma, sihirbazla kurulum, mağaza seçimi ve ilk siparişi alma.',
        roles: MANAGERS,
        feature: FeatureKey.FOOD_DELIVERY,
        videoUrl: '',
        steps: [
          {
            text: 'API bilgilerinizi alın: Trendyol Satıcı Paneli > "Hesap Bilgilerim" > "Entegrasyon Bilgileri" ekranında "API Key", "API Secret" ve "Satıcı ID" (supplierId) yazar. Bu üç bilgiyi not edin.',
            note: 'Satıcı ID sayısal bir değerdir ve mağaza numarasından farklıdır. Mağaza numarasını bu adımda aramanıza gerek yok; panel sizin için listeleyecek.'
          },
          {
            text: 'Panelde "Yemek Platformları" ekranını açıp "Yeni Entegrasyon"a tıklayın. Açılan sihirbazın ilk adımında platform olarak "Trendyol GO" seçin, ardından ortamı belirleyin: gerçek siparişler için "Canlı", deneme için "Test".',
            warning:
              'Test ortamı IP yetkilendirmesi ister: sunucunuzun ağ çıkış IP adresi Trendyol tarafında tanımlı değilse istekler 503 döner. Bu durumda Trendyol GO entegrasyon destek hattını (0850 258 58 00) arayıp IP adresinizi tanımlatın. Canlı ortam IP yetkilendirmesi istemez; canlıda erişim sorunu yaşarsanız IP adresinizle birlikte Trendyol satıcı panelinden bildirim oluşturun.'
          },
          {
            text: 'İkinci adımda API Key, API Secret ve Satıcı ID alanlarını doldurup kaydedin. Her alanın altındaki açıklama, bilginin panelde nerede yazdığını söyler.'
          },
          {
            text: 'Kaydettikten sonra sihirbaz mağazalarınızı Trendyol’dan otomatik çeker; listeden bu şubeye karşılık gelen mağazayı seçin. Bu adım ZORUNLUDUR — "Sonra seçerim" ile atlarsanız entegrasyon kurulum durumunda kalır ve SİPARİŞ ALMAZ.',
            warning:
              'Mağaza seçilmeden sipariş çekilmez: Trendyol’a mağaza belirtilmeden sorulduğunda satıcı numaranıza bağlı TÜM şubelerin siparişleri döner ve bu şubenin mutfağına düşerdi. Aynı nedenle fiyat gönderimi de engellenir (mağazasız gönderilen fiyat tüm restoranlarda geçerli olur). Menü çekme, uygunluk senkronu ve restoran durumu da çalışmaz. Entegrasyon listesinde bu durum "Mağaza seçilmedi" rozetiyle görünür.'
          },
          {
            text: 'Davranış ayarları adımında otomatik kabul, stok düşümü, hazırlama süresi ve yoklama aralığı gibi tercihleri belirleyin. Emin değilseniz varsayılanlarla devam edin.',
            note: 'Otomatik kabul varsayılan olarak KAPALIDIR. Açarsanız gelen sipariş operatöre sorulmadan kabul edilir ve platforma "hazırlanıyor" bilgisi gönderilir.'
          },
          {
            text: 'Son adımda "Doğrula ve bitir" ile bağlantıyı sınayın. "Bağlantı başarılı" görürseniz entegrasyon Aktif olur.',
            warning:
              'Doğrulama başarısız olursa hata mesajının sonundaki "talep no"yu yanındaki kopyala düğmesiyle alın; Trendyol destek ekibi başvuruda bu numarayı ister.'
          },
          {
            text: '"Menü Eşleştirme" ekranından platform menüsünü içe aktarın ve ürünleri kendi ürünlerinizle eşleştirin.',
            note: 'Eşleşmeyen ürün içeren sipariş otomatik olarak mutfağa düşmez, manuel incelemeye alınır. Kurulumdan sonra eşleştirmeyi tamamlamak önemlidir.'
          },
          {
            text: 'Siparişleri "Yemek Siparişleri" ekranından takip edin.',
            note: 'Trendyol GO bildirim (webhook) göndermez; siparişler yaklaşık 30 saniyede bir yapılan yoklama ile alınır. Bu yüzden sipariş ekrana birkaç saniye gecikmeyle düşebilir. Her yoklamada son 30 dakika taranır (aynı sipariş tekrar yazılmaz); sunucu bir süre kapalı kaldıysa pencere kendiliğinden 24 saate genişler.'
          },
          {
            text: 'Uzun bir kesinti sonrası siparişler eksikse "Sağlık & İzleme" ekranındaki "Siparişleri Geri Getir" düğmesiyle seçtiğiniz süreyi (en fazla 24 saat) platformdan yeniden tarayın.',
            note: 'Zaten kayıtlı siparişler çoğaltılmaz; yalnız eksik olanlar eklenir. Aynı ekranda "Son Yoklama" sütunu entegrasyonun canlılığını gösterir — Trendyol GO webhook göndermediği için "Son Webhook" bu platformda hep boş kalır.'
          },
          {
            text: 'Daha sonra bilgileri güncellemek isterseniz entegrasyonu düzenleyin. Boş bıraktığınız kimlik alanları KORUNUR; yalnız doldurduğunuz alanlar değişir.',
            warning:
              'Sadece API Key’i yenilerken diğer alanları boş bırakmanız güvenlidir — eski API Secret ve Satıcı ID silinmez.'
          }
        ],
        tips: [
          'Sık karşılaşılan hatalar: 401 → API Key/Secret hatalı ya da eksik. 403 → istek başlığı reddedildi, entegrasyon bilgilerini kontrol edin. 503 → test ortamında IP adresiniz yetkili değil. 429 → istek limiti; panel otomatik bekleyip tekrar dener, işlem yapmanız gerekmez.',
          'Fiyat güncellemesi Trendyol tarafında kuyruğa alınır. Senkron ekranında durum kısa süre "Platformda İşleniyor" görünür; sonuç öğrenilince başarılı kalemler senkronlanmış sayılır, reddedilen kalemler bir sonraki turda yeniden gönderilir.',
          'Trendyol tek fiyat isteğinde en fazla 1000 kalem kabul eder; büyük menülerde gönderim otomatik olarak parçalara bölünür ve her parça ayrı takip edilir. Bu yüzden bir senkronda birden fazla "Platformda İşleniyor" kaydı görebilirsiniz.',
          'Aynı Satıcı ID altındaki tüm şubeler Trendyol’un istek limitini paylaşır. Çok şubeli kurulumlarda senkronlar sıraya alınarak yürütülür; bu normaldir.',
          'Entegrasyon listesindeki hata notu, entegrasyon henüz hataya düşmemişken de görünür (örn. arka arkaya 2 başarısız yoklama). Sorunu eşiğe ulaşmadan fark etmenizi sağlar. Notta "Bilinmeyen paket statüsü" yazıyorsa Trendyol tanımadığımız yeni bir sipariş statüsü göndermiştir; o paket yanlış işlenmemek için bilerek atlanır ve entegrasyon çalışmaya devam eder — bu notu bize iletin.'
        ]
      },
      {
        id: 'yemek-siparisleri',
        title: 'Yemek Siparişlerini Yönetme',
        summary:
          'Platformlardan gelen siparişleri kabul etme, reddetme ve hazır bildirme.',
        roles: MANAGERS,
        feature: FeatureKey.FOOD_DELIVERY,
        videoUrl: '',
        steps: [
          {
            text: 'Sol menüden "Yemek Platformları > Yemek Siparişleri"ne girin. Platformlardan gelen siparişler burada listelenir.'
          },
          {
            text: 'Bir siparişi "Kabul et (mutfağa düşür)" ile onaylayın; kabul edilen sipariş mutfağa iletilir.',
            note: 'Eşleşmeyen ürün varsa sipariş manuel incelemeye düşer; menü eşleştirmesini kontrol edin.'
          },
          {
            text: 'Gerektiğinde siparişi "Reddet"; hazır olduğunda "Hazır bildir"; platform iptali için "İptal onayla" işlemlerini kullanın.'
          }
        ]
      },
      {
        id: 'yemek-iadeleri',
        title: 'Müşteri İadelerini Yanıtlama',
        summary:
          'Platformda açılan iadeleri 4 saatlik süre dolmadan kabul etme veya reddetme.',
        roles: MANAGERS,
        feature: FeatureKey.FOOD_DELIVERY,
        videoUrl: '',
        steps: [
          {
            text: 'Sol menüden "Yemek Platformları > Müşteri İadeleri"ne girin ve şubeyi seçin. Liste, yanıt süresi en yakın olan iade en üstte olacak şekilde sıralanır.',
            warning:
              'Trendyol GO’da iadeye 4 saat içinde yanıt verilmelidir. Süre dolarsa platform iadeyi satıcı aleyhine sonuçlandırabilir; bu doğrudan gelir kaybıdır.'
          },
          {
            text: '"Kalan Süre" sütununa bakın: 1 saatin altına düşen iadeler sarı, süresi dolanlar kırmızı "Süre doldu" olarak görünür. Süresi dolmuş iade varsa listenin üstünde kırmızı bir uyarı çıkar.'
          },
          {
            text: '"Neden" sütununda müşterinin iade gerekçesi ve varsa notu yazar; "İade Türü" ise para iadesi mi yoksa ürün telafisi mi yapılacağını gösterir.',
            note: '"Tam iade" siparişin tamamını, "kısmi iade" yalnız ilgili kalemi kapsar.'
          },
          {
            text: 'İade haklıysa "Kabul" düğmesine basın. İade platformda kapanır ve para iadesi / ürün telafisi süreci başlar.'
          },
          {
            text: 'İadeye itiraz edecekseniz "Reddet" düğmesine basın, açılan pencereden bir ret nedeni seçin ve isterseniz not ekleyin.',
            note: 'Ret nedeni listesi platform tarafından belirlenir; liste dışında bir neden gönderilemez. Reddedilen iadeyi platform inceleyip nihai kararı verir.'
          },
          {
            text: 'İadeler yaklaşık 5 dakikada bir otomatik olarak platformdan çekilir. Hemen görmek isterseniz "Platformdan Yenile" düğmesini kullanın.'
          },
          {
            text: '"Sağlık & İzleme" ekranında süresi yaklaşan/dolmuş iadeler için uyarı üretilir; günlük kontrolünüzü oradan da yapabilirsiniz.'
          }
        ],
        tips: [
          'Süresi dolan iade için "Kabul/Reddet" düğmeleri hâlâ görünebilir ancak platform isteği reddedebilir; bu durumda hata mesajı ekranda gösterilir.',
          'Ürün telafisi (PRODUCT_RESEND) türündeki iadelerde müşteriye para değil ürün gönderilir; kabul etmeden önce stok ve kurye durumunu değerlendirin.'
        ]
      },
      {
        id: 'yemek-kategori-fatura-degerlendirme',
        title: 'Kategori Kapatma, Fatura ve Değerlendirmeler',
        summary:
          'Kategoriyi topluca satışa kapatma, fatura bağlantısı gönderme ve yorumlara cevap yazma.',
        roles: MANAGERS,
        feature: FeatureKey.FOOD_DELIVERY,
        videoUrl: '',
        steps: [
          {
            text: '"Restoran Operasyonları" ekranının altındaki "Menü Kategorileri" bölümünden bir kategoriyi anahtarla satışa kapatabilirsiniz (örn. tatlılarınız bittiğinde).',
            note: 'Kategori kapatmak, altındaki tüm ürünleri platformda satışa kapatır; ürünleri tek tek kapatmaktan hem hızlı hem de geri almak kolaydır.'
          },
          {
            text: 'Kategori listesi platform menüsü içe aktarıldığında dolar. Liste boşsa önce "Menü Eşleştirme" ekranından menüyü içe aktarın.',
            note: 'Kapattığınız kategori, menüyü tekrar içe aktarsanız da kapalı kalır.'
          },
          {
            text: 'Fatura bağlantısı göndermek için "Yemek Siparişleri" ekranında siparişin detayını açın, "Fatura Bağlantısı" alanına faturanın adresini yazıp "Gönder"e basın.',
            warning:
              'Gönderdiğiniz bağlantının yasal olarak 10 yıl erişilebilir kalması gerekir. Geçici/kısa ömürlü adresler kullanmayın.'
          },
          {
            text: 'Yanlış bağlantı gönderdiyseniz aynı ekrandan "Sil" ile kaldırıp doğrusunu yeniden gönderebilirsiniz.'
          },
          {
            text: '"Değerlendirmeler" ekranından restoranınızın puan ortalamasını ve müşteri yorumlarını görün.',
            note: 'Yeterli değerlendirme birikmediyse (platform kuralı: 3’ten az) puan ortalaması gösterilmez; bu bir hata değildir.'
          },
          {
            text: 'Bir yoruma cevap yazmak için "Cevap yaz" düğmesini kullanın. Cevabınız platform onayından sonra yayınlanır.',
            warning:
              'Uygunsuz bulunan cevaplar reddedilir ve ekranda "Reddedildi" olarak red nedeniyle birlikte görünür. Cevaplar sonradan değiştirilemez.'
          }
        ],
        tips: [
          'Müşteriler siparişten sonra yalnız 7 gün içinde değerlendirme yapabilir; sonrasında yorum eklenemez ve mevcut yorumlar değiştirilemez.',
          'Kategori durumu, ancak platform isteği onayladıktan sonra panelde değişir. Böylece gerçekte satışta olan bir kategori panelde yanlışlıkla "kapalı" görünmez.'
        ]
      },
      {
        id: 'yemek-eslestirme-diger',
        title: 'Menü Eşleştirme, Fiyat & Diğer Araçlar',
        summary:
          'Ürün eşleştirme, fiyat/uygunluk, operasyonlar, izleme ve toplu yönetim.',
        roles: MANAGERS,
        feature: FeatureKey.FOOD_DELIVERY,
        videoUrl: '',
        steps: [
          {
            text: '"Menü Eşleştirme": Platformdaki ürünleri kendi ürünlerinizle eşleştirin. Doğru eşleştirme, gelen siparişlerin otomatik işlenmesini sağlar.'
          },
          {
            text: '"Fiyat & Uygunluk": Ürünlerin platformdaki fiyatlarını ve satışta olup olmadığını (uygunluk) senkronize edin.'
          },
          {
            text: '"Restoran Operasyonları": Restoranı platformda açma/kapama gibi operasyonel işlemler.'
          },
          {
            text: '"Sağlık & İzleme" entegrasyonun durumunu izler; "Toplu Yönetim" ile çok sayıda ürün/ayarı topluca güncellersiniz.'
          }
        ]
      }
    ]
  },

  /* ═══════════════════════════════════════════════════════════════
     ⑯ MASAÜSTÜ UYGULAMASI
     ═══════════════════════════════════════════════════════════════ */
  {
    id: 'masaustu-uygulamasi',
    title: 'Masaüstü Uygulaması',
    icon: 'Computer',
    articles: [
      {
        id: 'masaustu-indirme',
        title: 'Masaüstü Uygulamasını İndirme',
        summary: 'Yazdırma ve sipariş takibi için masaüstü uygulamasını kurma.',
        roles: ALL_STAFF,
        feature: FeatureKey.DESKTOP_APP,
        videoUrl: 'https://youtu.be/u6oEQtzVWPs',
        steps: [
          {
            text: 'Sol menüden "İndirmeler > Masaüstü Uygulaması"na girin.'
          },
          {
            text: 'İşletim sisteminize uygun sürümü indirin: Windows (.exe), macOS (.dmg) veya Linux (.deb / .AppImage). Sürüm henüz yoksa düğme "Yakında" görünür.'
          },
          {
            text: 'İndirilen dosyayı çalıştırıp kurulumu tamamlayın, ardından şube bilgilerinizle giriş yapın ve yazıcı ayarlarını yapılandırın.',
            note: 'Masaüstü uygulaması otomatik yazdırma (siparişlerin termal yazıcıya gönderilmesi), sipariş takibi ve şube yönetimi için kullanılır.'
          }
        ]
      }
    ]
  }
];
